import React, { useEffect, useMemo, useRef, useState, useCallback, memo } from "react";
import {
  Box,
  Badge,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Alert,
  Skeleton,
  TextField,
  IconButton,
  Popover,
  Button,
  InputAdornment,
  Tooltip,
  Checkbox,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  Download,
  Search as SearchIcon,
  PersonOutline,
  Close,
  MailOutline,
  ContentCopy,
  DeleteOutline,
  Remove,
  Add,
  ShoppingCart,
  AddShoppingCart,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import {
  confirmReceiveForniturasPedido,
  confirmReceiveForniturasPedidoLinea,
  confirmReceiveForniturasPedidoProveedor,
  createForniturasPedido,
  getForniturasPedidoDetail,
  getStockFornituras,
  listForniturasPedidos,
  updateForniturasPedidoLineaCantidad,
} from "../../services/ConnectDataBase.js";
import { translateText } from "../../translations/translationService";
import { useSelector } from "react-redux";
import { BORDER_COLORS, BRAND_COLORS, STATUS_COLORS } from "../../style/colors.js";
import { notify } from "../../services/ui/notify";

const formatCell = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") {
    if (value.date !== undefined) return String(value.date);
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const parseNumeric = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "object") {
    if (value.date !== undefined) return null;
    return null;
  }
  const s = String(value).trim().replace(",", ".");
  if (!s) return null;
  const normalized =
    s.startsWith(".") || s.startsWith("-.")
      ? s.startsWith("-")
        ? `-0${s.slice(1)}`
        : `0${s}`
      : s;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

const formatNumeric = (value) => {
  const n = parseNumeric(value);
  if (n === null) return "—";
  return n.toFixed(2);
};

const formatQuantityValue = (value) => {
  const n = parseNumeric(value);
  if (n === null) return "—";

  return Number.isInteger(n)
    ? String(n)
    : n.toLocaleString("es-ES", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
};

const formatEmailQuantityValue = (value) => {
  const n = parseNumeric(value);
  if (n === null) return "";
  const asString = String(n);
  return asString.includes(".") ? asString.replace(/0+$/, "").replace(/\.$/, "") : asString;
};

const getLineReceivedTotal = (item) => {
  const explicitReceived = parseNumeric(item?.recibido_total);
  if (explicitReceived !== null) return explicitReceived;
  if (item?.recibido_fecha || item?.recibidoFecha) {
    return parseNumeric(item?.cantidad) ?? 0;
  }
  return 0;
};

const getLinePendingQuantity = (item) => {
  const explicitPending = parseNumeric(item?.pendiente);
  if (explicitPending !== null) return explicitPending;

  const total = parseNumeric(item?.cantidad) ?? 0;
  const received = getLineReceivedTotal(item);
  return Math.max(total - received, 0);
};

const getLineMutationKey = (batchUid, item) =>
  `${batchUid}::${Number(item?.linea_id) || item?.linea_n || item?.codigo || ""}`;

const getHistoryItemProviderName = (item) =>
  String(item?.nombre ?? item?.proveedorNombre ?? item?.proveedor ?? "").trim();

const detailLineActionButtonSx = {
  minWidth: 0,
  minHeight: 26,
  px: 0.75,
  py: 0,
  fontSize: "0.66rem",
  lineHeight: 1.1,
  textTransform: "none",
  whiteSpace: "nowrap",
};

const getCellDisplayString = (row, col) => {
  if (col.numeric) return formatNumeric(row[col.id]);
  return formatCell(row[col.id]);
};

const getCellNumericForExcel = (row, col) => {
  if (col.numeric) {
    const n = parseNumeric(row[col.id]);
    if (n === null) return null;
    return Number(n.toFixed(2));
  }
  return null;
};

/** Hay demanda pero no hay stock disponible en ninguna ubicación. */
const isCordobaRequirementWithoutStock = (row) => {
  const u = parseNumeric(row?.unidades);
  const s90 = parseNumeric(row?.sto_1990);
  const s87 = parseNumeric(row?.sto_1987);
  const sEur = parseNumeric(row?.stock_eur);
  return u !== null && u > 0 && s90 === 0 && s87 === 0 && sEur === 0;
};

const cordobaRequirementGapRowSx = {
  backgroundColor: "rgba(255, 193, 7, 0.12)",
  boxShadow: `inset 3px 0 0 0 ${STATUS_COLORS.WARNING}`,
  "&:nth-of-type(even)": {
    backgroundColor: "rgba(255, 193, 7, 0.18)",
  },
  "&:hover": {
    backgroundColor: "rgba(255, 193, 7, 0.28)",
  },
};

const MERGED_COLUMNS = [
  { id: "codigo", labelKey: "Código", numeric: false, width: "28%" },
  { id: "unidades", labelKey: "Unidades", numeric: true, width: "12%" },
  { id: "sto_1990", labelKey: "STO 1990", numeric: true, width: "12%" },
  { id: "sto_1987", labelKey: "STO 1987", numeric: true, width: "12%" },
  { id: "stock_eur", labelKey: "StockEur", numeric: true, width: "12%" },
  { id: "stock_wt", labelKey: "StockEurWt", numeric: true, width: "12%" },
  { id: "total", labelKey: "Total", numeric: true, width: "12%" },
  { id: "unidades_pedidas_pendientes", labelKey: "Cant. Ped.", numeric: true, width: "12%" },
];

const compactCellSx = { py: 0.5, px: 1, fontSize: "0.8rem" };
const compactHeadSx = {
  py: 0.75,
  px: 1,
  fontWeight: 700,
  fontSize: "0.72rem",
  backgroundColor: "#f0f4f5",
};

const getColumnCellSx = (col, isHeader = false) => ({
  ...(isHeader ? compactHeadSx : compactCellSx),
  width: col.width,
  maxWidth: col.width,
  ...(col.id === "codigo"
    ? {
        whiteSpace: "normal",
        wordBreak: "break-all",
        overflowWrap: "anywhere",
      }
    : {
        whiteSpace: "nowrap",
      }),
});

/** Filtro por búsqueda global + sets por columna (estilo Excel). `ignoreColumnId` excluye esa columna del filtro por valores (para listar únicos). */
const filterRowsWithValueFilters = (
  sourceRows,
  columns,
  globalSearch,
  columnValueFilters,
  ignoreColumnId = null
) => {
  const q = globalSearch.trim().toLowerCase();
  return sourceRows.filter((row) => {
    if (q) {
      const haystack = columns
        .map((c) => getCellDisplayString(row, c).toLowerCase())
        .join("\u0000");
      if (!haystack.includes(q)) return false;
    }
    for (const col of columns) {
      if (col.id === ignoreColumnId) continue;
      const allowed = columnValueFilters[col.id];
      if (allowed == null) continue;
      const v = getCellDisplayString(row, col);
      if (!allowed.has(v)) return false;
    }
    return true;
  });
};

const getUniqueSortedValues = (rows, col) => {
  const set = new Set();
  rows.forEach((row) => {
    set.add(getCellDisplayString(row, col));
  });
  return Array.from(set).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
  );
};

const isFullSelection = (draft, uniq) => {
  if (uniq.length === 0) return true;
  if (draft.size !== uniq.length) return false;
  return uniq.every((v) => draft.has(v));
};

const exportRowsToExcel = (displayRows, columns, lang, sheetName, fileBase) => {
  if (!displayRows.length) {
    notify.info(translateText("No se encontraron datos.", lang));
    return;
  }

  const headers = columns.map((c) => translateText(c.labelKey, lang));
  const data = displayRows.map((row) => {
    const obj = {};
    columns.forEach((col, i) => {
      const h = headers[i];
      if (col.numeric) {
        const n = getCellNumericForExcel(row, col);
        obj[h] = n !== null && n !== undefined && !Number.isNaN(n) ? n : "";
      } else {
        obj[h] = formatCell(row[col.id]);
      }
    });
    return obj;
  });

  const worksheet = XLSX.utils.json_to_sheet(data);
  const range = XLSX.utils.decode_range(worksheet["!ref"]);

  const numericColIndexes = columns
    .map((c, i) => (c.numeric ? i : -1))
    .filter((i) => i >= 0);

  for (let R = 1; R <= range.e.r; R++) {
    for (const c of numericColIndexes) {
      const ref = XLSX.utils.encode_cell({ r: R, c });
      const cell = worksheet[ref];
      if (cell && typeof cell.v === "number" && Number.isFinite(cell.v)) {
        worksheet[ref] = { t: "n", v: cell.v, z: "0.00" };
      }
    }
  }

  worksheet["!autofilter"] = { ref: XLSX.utils.encode_range(range) };

  const wch = (arr, min = 8) =>
    arr.length ? Math.max(min, ...arr.map((s) => String(s ?? "").length)) : min;
  worksheet["!cols"] = headers.map((h) => ({
    wch: Math.max(
      h.length + 2,
      data.length
        ? wch(
            data.map((r) => r[h] ?? ""),
            10
          )
        : 10
    ),
  }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.slice(0, 31));
  const date = new Date().toISOString().split("T")[0];
  XLSX.writeFile(workbook, `${fileBase}_${date}.xlsx`);
};

const StockTable = memo(({
  title,
  sourceRows,
  lang,
  loading,
  columns,
  exportFileBase,
  sheetName,
  highlightCordobaRequirementGap = false,
  onCreateOrderFromRow,
  onAppendRowToOrder,
  onRemoveOneFromOrder,
  orderDraftQuantities = {},
}) => {
  const hasRowOrderActions = Boolean(onCreateOrderFromRow || onAppendRowToOrder);
  const colCount = columns.length + (hasRowOrderActions ? 1 : 0);
  const [globalSearch, setGlobalSearch] = useState("");
  /** null = sin filtro en esa columna; Set = solo filas cuyo valor mostrado está en el set */
  const [columnValueFilters, setColumnValueFilters] = useState({});
  const [filterMenu, setFilterMenu] = useState(null);
  const [filterListSearch, setFilterListSearch] = useState("");
  const [draftSelection, setDraftSelection] = useState(() => new Set());
  const [allUniquesForOpenColumn, setAllUniquesForOpenColumn] = useState([]);
  const [sortColId, setSortColId] = useState("codigo");
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"
  const [draftSortDir, setDraftSortDir] = useState("asc");

  const filteredRows = useMemo(() => {
    const base = filterRowsWithValueFilters(
      sourceRows,
      columns,
      globalSearch,
      columnValueFilters,
      null
    );

    const dir = sortDir === "desc" ? -1 : 1;
    const sortCol = columns.find((c) => c.id === sortColId);

    const withIndex = base.map((row, idx) => ({ row, idx }));
    withIndex.sort((a, b) => {
      if (!sortCol) return a.idx - b.idx;

      if (sortCol.id === "codigo") {
        const av = String(a.row?.codigo ?? "").toLowerCase();
        const bv = String(b.row?.codigo ?? "").toLowerCase();
        const c = av.localeCompare(bv, undefined, { numeric: true, sensitivity: "base" });
        return c !== 0 ? c * dir : a.idx - b.idx;
      }

      const an = parseNumeric(a.row?.[sortCol.id]);
      const bn = parseNumeric(b.row?.[sortCol.id]);
      const aVal = an == null ? -Infinity : an;
      const bVal = bn == null ? -Infinity : bn;
      if (aVal === bVal) return a.idx - b.idx;
      return (aVal < bVal ? -1 : 1) * dir;
    });

    return withIndex.map((x) => x.row);
  }, [sourceRows, columns, globalSearch, columnValueFilters, sortColId, sortDir]);

  const sortDirectionOptions = useMemo(() => {
    const currentColId = filterMenu?.colId || sortColId;
    if (currentColId === "codigo") {
      return [
        { id: "asc", label: "A - Z" },
        { id: "desc", label: "Z - A" },
      ];
    }
    return [
      { id: "asc", label: "Menor a mayor" },
      { id: "desc", label: "Mayor a menor" },
    ];
  }, [filterMenu, sortColId]);

  const openColumnFilter = useCallback((colId, anchorEl) => {
    setFilterMenu({ colId, anchorEl });
  }, []);

  useEffect(() => {
    if (!filterMenu) {
      setFilterListSearch("");
      setAllUniquesForOpenColumn([]);
      setDraftSortDir("asc");
      return;
    }
    const col = columns.find((c) => c.id === filterMenu.colId);
    if (!col) return;

    const baseRows = filterRowsWithValueFilters(
      sourceRows,
      columns,
      globalSearch,
      columnValueFilters,
      filterMenu.colId
    );
    const uniq = getUniqueSortedValues(baseRows, col);
    setAllUniquesForOpenColumn(uniq);

    const existing = columnValueFilters[filterMenu.colId];
    if (existing == null) {
      setDraftSelection(new Set(uniq));
    } else {
      const next = new Set();
      uniq.forEach((v) => {
        if (existing.has(v)) next.add(v);
      });
      setDraftSelection(next);
    }
    setDraftSortDir(
      sortColId === filterMenu.colId ? sortDir : "asc"
    );
    setFilterListSearch("");
  }, [filterMenu, sourceRows, columns, globalSearch, columnValueFilters, sortColId, sortDir]);

  const visibleUniques = useMemo(() => {
    const q = filterListSearch.trim().toLowerCase();
    if (!q) return allUniquesForOpenColumn;
    return allUniquesForOpenColumn.filter((v) => v.toLowerCase().includes(q));
  }, [allUniquesForOpenColumn, filterListSearch]);

  const toggleValue = (value) => {
    setDraftSelection((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    const allVisSelected =
      visibleUniques.length > 0 && visibleUniques.every((v) => draftSelection.has(v));
    setDraftSelection((prev) => {
      const next = new Set(prev);
      if (allVisSelected) {
        visibleUniques.forEach((v) => next.delete(v));
      } else {
        visibleUniques.forEach((v) => next.add(v));
      }
      return next;
    });
  };

  const applyColumnFilter = () => {
    if (!filterMenu) return;
    const colId = filterMenu.colId;
    if (
      allUniquesForOpenColumn.length === 0 ||
      isFullSelection(draftSelection, allUniquesForOpenColumn)
    ) {
      setColumnValueFilters((prev) => {
        const next = { ...prev };
        delete next[colId];
        return next;
      });
    } else {
      setColumnValueFilters((prev) => ({
        ...prev,
        [colId]: new Set(draftSelection),
      }));
    }
    setSortColId(colId);
    setSortDir(draftSortDir);
    setFilterMenu(null);
  };

  const clearColumnFilter = () => {
    if (!filterMenu) return;
    const colId = filterMenu.colId;
    setColumnValueFilters((prev) => {
      const next = { ...prev };
      delete next[colId];
      return next;
    });
    setFilterMenu(null);
  };

  const handleExport = () => {
    exportRowsToExcel(filteredRows, columns, lang, sheetName, exportFileBase);
  };

  const renderCell = (row, col) => {
    if (col.numeric) {
      return formatNumeric(row[col.id]);
    }
    return formatCell(row[col.id]);
  };

  const searchField = (
    <TextField
      size="small"
      placeholder={translateText("Buscar por cualquier campo...", lang)}
      value={globalSearch}
      onChange={(e) => setGlobalSearch(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ color: "rgba(0,0,0,0.45)", fontSize: 20 }} />
          </InputAdornment>
        ),
        sx: {
          backgroundColor: "rgba(255,255,255,0.96)",
          borderRadius: 1,
          fontSize: "0.8rem",
        },
      }}
      sx={{
        minWidth: { xs: "100%", sm: 200 },
        maxWidth: { sm: 280 },
        flex: { xs: "1 1 100%", sm: "0 1 auto" },
      }}
    />
  );

  const allVisibleSelected =
    visibleUniques.length > 0 && visibleUniques.every((v) => draftSelection.has(v));
  const someVisibleSelected = visibleUniques.some((v) => draftSelection.has(v));
  const hasActiveFilters = Boolean(globalSearch.trim()) || Object.keys(columnValueFilters).length > 0;
  const lineCountLabel = hasActiveFilters
    ? `${filteredRows.length} líneas de ${sourceRows.length}`
    : `${filteredRows.length} líneas`;

  return (
    <Paper
      elevation={1}
      sx={{
        width: "100%",
        minWidth: 0,
        minHeight:"400px",
        display: "flex",
        flexDirection: "column",
        borderRadius: 1,
        overflow: "hidden",
        border: `1px solid ${BORDER_COLORS.LIGHT}`,
      }}>
      <Box
        sx={{
          px: 1.5,
          py: 1,
          backgroundColor: BRAND_COLORS.PRIMARY_LIGHT || "#7aa5b3",
          color: "#fff",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 1,
        }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ flexShrink: 0 }}>
            {translateText(title, lang)}
          </Typography>
          <Box
            sx={{
              px: 0.85,
              py: 0.2,
              borderRadius: 999,
              backgroundColor: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.28)",
            }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, lineHeight: 1.2, color: "#fff" }}>
              {lineCountLabel}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexWrap: "wrap",
            justifyContent: { xs: "flex-start", sm: "flex-end" },
          }}>
          {searchField}
          <Tooltip title={translateText("Exportar a Excel", lang)}>
            <IconButton
              size="small"
              onClick={handleExport}
              sx={{ color: "#fff" }}
              aria-label={translateText("Exportar a Excel", lang)}>
              <Download fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <TableContainer
        sx={{
          maxHeight: "70vh",
          width: "100%",
          flex: 1,
          minHeight: 0,
        }}>
        <Table size="small" stickyHeader sx={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => {
                const hasActiveFilter = columnValueFilters[col.id] != null;
                return (
                  <TableCell
                    key={col.id}
                    onClick={(e) => openColumnFilter(col.id, e.currentTarget)}
                    sx={{
                      ...getColumnCellSx(col, true),
                      cursor: "pointer",
                      userSelect: "none",
                      ...(hasActiveFilter
                        ? {
                            boxShadow: `inset 0 -3px 0 ${BRAND_COLORS.ACCENT}`,
                          }
                        : {}),
                      "&:hover": {
                        backgroundColor: "#e4e8ea",
                      },
                    }}
                    aria-label={translateText("Filtrar columna", lang)}>
                    <Typography
                      component="span"
                      variant="inherit"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "block",
                      }}>
                      {translateText(col.labelKey, lang)}
                    </Typography>
                  </TableCell>
                );
              })}
              {hasRowOrderActions ? (
                <TableCell
                  sx={{
                    ...compactHeadSx,
                    width: 92,
                    minWidth: 92,
                    maxWidth: 92,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}>
                  <Typography
                    component="span"
                    variant="inherit"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      display: "block",
                    }}>
                    Pedido
                  </Typography>
                </TableCell>
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {columns.map((col) => (
                      <TableCell key={col.id} sx={getColumnCellSx(col)}>
                        <Skeleton variant="text" height={18} width={56} />
                      </TableCell>
                    ))}
                    {hasRowOrderActions ? (
                      <TableCell sx={{ py: 0.5, px: 0.75 }}>
                        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.25 }}>
                          <Skeleton variant="circular" width={22} height={22} />
                          <Skeleton variant="circular" width={22} height={22} />
                        </Box>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))
              : filteredRows.map((row, idx) => {
                  const isGapAlert =
                    highlightCordobaRequirementGap && isCordobaRequirementWithoutStock(row);
                  const draftQuantity = Math.max(0, parseNumeric(orderDraftQuantities?.[row?.codigo]) || 0);
                  const isInDraft = draftQuantity > 0;
                  return (
                    <TableRow
                      key={idx}
                      hover
                      sx={
                        isGapAlert
                          ? cordobaRequirementGapRowSx
                          : { "&:nth-of-type(even)": { backgroundColor: "#fafafa" } }
                      }>
                      {columns.map((col) => (
                        <TableCell key={col.id} sx={getColumnCellSx(col)}>
                          {renderCell(row, col)}
                        </TableCell>
                      ))}
                      {hasRowOrderActions ? (
                        <TableCell sx={{ py: 0.35, px: 0.45 }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.15 }}>
                            <Tooltip title="Hacer pedido">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => onCreateOrderFromRow?.(row)}
                                  sx={{
                                    color: isInDraft ? "#1565c0" : "rgba(23, 64, 69, 0.74)",
                                    backgroundColor: isInDraft ? "rgba(21, 101, 192, 0.08)" : "transparent",
                                    p: 0.55,
                                    "&:hover": {
                                      backgroundColor: "rgba(21, 101, 192, 0.14)",
                                    },
                                  }}>
                                  <ShoppingCart sx={{ fontSize: 18 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Añadir al pedido. Clic derecho: quitar una">
                              <span>
                                <Badge
                                  badgeContent={draftQuantity}
                                  color="success"
                                  overlap="circular"
                                  invisible={draftQuantity <= 0}
                                  max={99}
                                  sx={{
                                    "& .MuiBadge-badge": {
                                      minWidth: 16,
                                      height: 16,
                                      px: 0.5,
                                      fontSize: "0.6rem",
                                      fontWeight: 800,
                                      borderRadius: 999,
                                      boxShadow: "0 0 0 1px #fff",
                                    },
                                  }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => onAppendRowToOrder?.(row)}
                                    onContextMenu={(event) => {
                                      event.preventDefault();
                                      onRemoveOneFromOrder?.(row);
                                    }}
                                    sx={{
                                      color: isInDraft ? "#2e7d32" : "rgba(23, 64, 69, 0.74)",
                                      backgroundColor: isInDraft ? "rgba(46, 125, 50, 0.08)" : "transparent",
                                      p: 0.55,
                                      "&:hover": {
                                        backgroundColor: "rgba(46, 125, 50, 0.14)",
                                      },
                                    }}>
                                    <AddShoppingCart sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Badge>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })}
            {!loading && filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={colCount} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary" variant="body2">
                    {translateText("No se encontraron datos.", lang)}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Popover
        open={Boolean(filterMenu)}
        anchorEl={filterMenu?.anchorEl}
        onClose={() => setFilterMenu(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: { mt: 0.5, maxWidth: "min(360px, 92vw)" },
          },
        }}>
        <Box sx={{ p: 1.5, width: 320, maxWidth: "92vw" }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {filterMenu
              ? translateText(columns.find((c) => c.id === filterMenu.colId)?.labelKey || "", lang)
              : ""}
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder={translateText("Buscar en lista de valores", lang)}
            value={filterListSearch}
            onChange={(e) => setFilterListSearch(e.target.value)}
            sx={{ mb: 1 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <Select
              value={draftSortDir}
              onChange={(e) => setDraftSortDir(e.target.value)}
              sx={{
                backgroundColor: "rgba(255,255,255,0.96)",
                borderRadius: 1,
                fontSize: "0.8rem",
              }}
              aria-label={translateText("Orden", lang)}
            >
              {sortDirectionOptions.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            sx={{ ml: 0, mb: 0.5, display: "flex", alignItems: "center" }}
            control={
              <Checkbox
                size="small"
                checked={allVisibleSelected}
                indeterminate={someVisibleSelected && !allVisibleSelected}
                onChange={toggleSelectAllVisible}
              />
            }
            label={
              <Typography variant="body2">
                {translateText("Seleccionar todo (visible)", lang)}
              </Typography>
            }
          />
          <Divider sx={{ my: 0.5 }} />
          <List
            dense
            sx={{
              maxHeight: 260,
              overflow: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              py: 0,
            }}>
            {visibleUniques.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary={translateText("No se encontraron datos.", lang)}
                  primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                />
              </ListItem>
            ) : (
              visibleUniques.map((val) => (
                <ListItem key={val} disablePadding>
                  <ListItemButton dense onClick={() => toggleValue(val)} sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        size="small"
                        tabIndex={-1}
                        checked={draftSelection.has(val)}
                        sx={{ pointerEvents: "none" }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={val || " "}
                      primaryTypographyProps={{
                        variant: "body2",
                        sx: { wordBreak: "break-all" },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </List>
          <Box sx={{ display: "flex", gap: 1, mt: 1.5, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <Button size="small" onClick={clearColumnFilter}>
              {translateText("Quitar filtro", lang)}
            </Button>
            <Button size="small" variant="contained" onClick={applyColumnFilter}>
              {translateText("Aplicar", lang)}
            </Button>
          </Box>
        </Box>
      </Popover>
    </Paper>
  );
});

const getRowCodigo = (row) =>
  String(row?.codigo ?? row?.Codigo ?? row?.CODIGO ?? "").trim();

const normalizeProviderName = (value) => String(value ?? "").replace(/\s+/g, " ").trim();

const getProviderIdentityKey = (provider) => {
  const proveedor = String(provider?.proveedor ?? "").trim();
  const nombre = normalizeProviderName(provider?.nombre ?? "");
  if (proveedor) return `proveedor::${proveedor}`;
  if (nombre) return `nombre::${nombre.toLowerCase()}`;
  return "";
};

const getProviderOptionFromRow = (row) => {
  const proveedor = row?.proveedor ?? row?.id_proveedor ?? row?.proveedor_id ?? "";
  const nombre = normalizeProviderName(row?.nombre ?? row?.proveedor_nombre ?? row?.nombprov ?? "");
  const referenciaprov = String(
    row?.referenciaprov ?? row?.referenciaProv ?? row?.referencia_proveedor ?? ""
  ).trim();
  const hasAnyProviderData = proveedor !== "" || nombre || referenciaprov;
  if (!hasAnyProviderData) return null;

  const identityKey = getProviderIdentityKey({ proveedor, nombre });
  if (!identityKey) return null;

  return {
    key: identityKey,
    proveedor,
    nombre: nombre || "—",
    referenciaprov,
  };
};

const mergeProviderOptions = (...groups) => {
  const uniqueProviders = new Map();

  groups.flat(Infinity).forEach((provider) => {
    const identityKey = getProviderIdentityKey(provider);
    if (!identityKey) return;
    if (!uniqueProviders.has(identityKey)) {
      uniqueProviders.set(identityKey, {
        ...provider,
        key: identityKey,
      });
    }
  });

  return Array.from(uniqueProviders.values()).sort((a, b) =>
    String(a?.nombre || a?.proveedor || "").localeCompare(String(b?.nombre || b?.proveedor || ""), undefined, {
      numeric: true,
      sensitivity: "base",
    })
  );
};

const sidePanelPaperSx = {
  width: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  borderRadius: 1,
  overflow: "hidden",
  border: `1px solid ${BORDER_COLORS.LIGHT}`,
};

const sidePanelHeaderSx = {
  px: 1.5,
  py: 1,
  backgroundColor: BRAND_COLORS.PRIMARY_LIGHT || "#7aa5b3",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  flexShrink: 0,
};

const compactInputSx = {
  "& .MuiInputBase-root": {
    backgroundColor: "rgba(255,255,255,0.98)",
    fontSize: "0.82rem",
    borderRadius: 1.2,
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.82rem",
  },
};

const orderFormShellSx = {
  display: "flex",
  flexDirection: "column",
  gap: 1.25,
  minWidth: 0,
};

const orderSectionSx = {
  border: `1px solid rgba(23, 64, 69, 0.18)`,
  borderRadius: 1.5,
  background: "rgba(246,249,250,0.99)",
  p: 0.9,
  boxShadow: "0 10px 22px rgba(23, 64, 69, 0.08)",
};

const orderSectionHeaderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  mb: 1,
};

const orderSectionTitleSx = {
  display: "block",
  color: BRAND_COLORS.PRIMARY_DARK || "#174045",
  fontSize: "0.74rem",
  fontWeight: 700,
  letterSpacing: "0.02em",
  textTransform: "uppercase",
};

const orderCompactFieldSx = {
  "& .MuiInputBase-root": {
    height: 28,
    backgroundColor: "#fff",
    fontSize: "0.74rem",
    borderRadius: 1,
    border: "1px solid rgba(23, 64, 69, 0.18)",
  },
  "& .MuiInputBase-input, & .MuiSelect-select": {
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    minHeight: "28px !important",
    height: "28px",
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    paddingLeft: "8px !important",
    paddingRight: "26px !important",
    fontSize: "0.74rem",
    lineHeight: 1.2,
  },
  "& .MuiSelect-icon": {
    fontSize: 18,
    right: 6,
  },
};

const orderSingleLineFieldSx = {
  "& .MuiInputBase-root": {
    height: 28,
    backgroundColor: "#fff",
    fontSize: "0.74rem",
    borderRadius: 1,
    border: "1px solid rgba(23, 64, 69, 0.18)",
  },
  "& .MuiInputBase-input": {
    boxSizing: "border-box",
    height: "28px",
    padding: "0 8px",
    fontSize: "0.74rem",
    lineHeight: 1.2,
  },
};

const SIDE_HISTORY_TABLE_HEIGHT = {
  xs: 160,
  xl: 360,
};

const orderNoteFieldSx = {
  "& .MuiInputBase-root": {
    backgroundColor: "#fff",
    fontSize: "0.72rem",
    borderRadius: 1.1,
    alignItems: "flex-start",
    border: "1px solid rgba(23, 64, 69, 0.18)",
  },
  "& .MuiInputBase-inputMultiline": {
    py: 0.45,
  },
};

const historyCellSx = {
  py: 0.75,
  px: 1,
  fontSize: "0.78rem",
  verticalAlign: "top",
};

const detailLineCellSx = {
  ...historyCellSx,
  verticalAlign: "middle",
};

const formatHistoryDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatHistoryDateParts = (value) => {
  if (!value) return { date: "—", time: "" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: String(value), time: "" };
  return {
    date: date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }),
    time: date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
};

const buildOrderSummary = (items) =>
  items
    .map((item) => {
      const qty = parseNumeric(item?.cantidad) || 0;
      const note = String(item?.nota ?? "").trim();
      const unit = String(item?.unidad ?? "Unidades").trim();
      return `${item.codigo} x${qty} ${unit}${note ? ` (${note})` : ""}`;
    })
    .join(" | ");

const buildOrderProviderSummary = (items) =>
  Array.from(
    new Set(
      (Array.isArray(items) ? items : [])
        .map((item) => String(item?.nombre ?? "").trim())
        .filter(Boolean)
    )
  ).join(", ") || "—";

const HISTORY_STATUS_META = {
  pendiente: {
    id: "pendiente",
    label: "Pendiente",
    textColor: "#8a5a00",
    bgColor: "rgba(255, 193, 7, 0.18)",
    borderColor: "rgba(255, 193, 7, 0.34)",
  },
  parcial: {
    id: "parcial",
    label: "Parcial",
    textColor: "#1565c0",
    bgColor: "rgba(25, 118, 210, 0.12)",
    borderColor: "rgba(25, 118, 210, 0.26)",
  },
  recibido: {
    id: "recibido",
    label: "Recibido",
    textColor: "#2e7d32",
    bgColor: "rgba(46, 125, 50, 0.14)",
    borderColor: "rgba(46, 125, 50, 0.26)",
  },
  incidencia: {
    id: "incidencia",
    label: "Incidencia",
    textColor: "#c62828",
    bgColor: "rgba(211, 47, 47, 0.12)",
    borderColor: "rgba(211, 47, 47, 0.24)",
  },
};

const getHistoryOrderStatus = ({ items, proveedores, recibidoFecha }) => {
  const safeItems = Array.isArray(items) ? items : [];
  const safeProviders = Array.isArray(proveedores) ? proveedores : [];
  const hasItems = safeItems.length > 0;
  const hasIncidence = safeItems.some((item) => {
    const qty = parseNumeric(item?.cantidad) ?? 0;
    const pending = parseNumeric(item?.pendiente) ?? getLinePendingQuantity(item);
    const received = parseNumeric(item?.recibido_total) ?? getLineReceivedTotal(item);
    return qty < 0 || pending < 0 || received > qty;
  });

  if (hasIncidence) return HISTORY_STATUS_META.incidencia;

  const hasAnyReceived =
    Boolean(recibidoFecha) ||
    safeProviders.some((provider) => Boolean(provider?.recibidoFecha)) ||
    safeItems.some((item) => {
      const received = parseNumeric(item?.recibido_total) ?? getLineReceivedTotal(item);
      return Boolean(item?.recibidoFecha) || received > 0;
    });

  const isFullyReceived =
    Boolean(recibidoFecha) ||
    (hasItems &&
      safeItems.every((item) => {
        const pending = parseNumeric(item?.pendiente) ?? getLinePendingQuantity(item);
        return pending <= 0;
      }));

  if (isFullyReceived) return HISTORY_STATUS_META.recibido;
  if (hasAnyReceived) return HISTORY_STATUS_META.parcial;
  return HISTORY_STATUS_META.pendiente;
};

const normalizeOrderProviders = (orderProviders, items) => {
  if (Array.isArray(orderProviders) && orderProviders.length > 0) {
    return orderProviders.map((provider) => ({
      proveedor: String(provider?.proveedor ?? provider?.nombre ?? "").trim(),
      recibidoFecha: provider?.recibido_fecha ?? null,
    }));
  }

  return Array.from(
    new Map(
      (Array.isArray(items) ? items : [])
        .map((item) => String(item?.nombre ?? "").trim())
        .filter(Boolean)
        .map((proveedor) => [proveedor, { proveedor, recibidoFecha: null }])
    ).values()
  );
};

const normalizeHistoryOrder = (order) => {
  const items = Array.isArray(order?.items)
    ? order.items.map((item) => ({
        ...item,
        cantidad: parseNumeric(item?.cantidad) ?? item?.cantidad ?? "",
        recibido_total: getLineReceivedTotal(item),
        pendiente: getLinePendingQuantity(item),
        recibidoFecha: item?.recibido_fecha ?? item?.recibidoFecha ?? null,
      }))
    : [];
  const proveedores = normalizeOrderProviders(order?.proveedores, items);
  const estado = getHistoryOrderStatus({
    items,
    proveedores,
    recibidoFecha: order?.recibido_fecha ?? null,
  });

  return {
    id: order?.pedido_id ?? order?.batch_uid ?? `pedido-${Date.now()}`,
    pedido_id: order?.pedido_id ?? null,
    batch_uid: String(order?.batch_uid ?? ""),
    fecha: order?.fecha_alta ?? "",
    proveedor: buildOrderProviderSummary(items),
    pedido: buildOrderSummary(items),
    usuario: String(order?.usuario ?? "—"),
    recibidoFecha: order?.recibido_fecha ?? null,
    estado,
    proveedores,
    items,
  };
};

const HistoryStatusBadge = ({ status }) => {
  const meta = status || HISTORY_STATUS_META.pendiente;
  return (
    <Chip
      label={meta.label}
      size="small"
      sx={{
        height: 20,
        fontSize: "0.64rem",
        fontWeight: 800,
        color: meta.textColor,
        backgroundColor: meta.bgColor,
        border: `1px solid ${meta.borderColor}`,
        "& .MuiChip-label": {
          px: 0.65,
        },
      }}
    />
  );
};

const HistoryFiltersBar = ({
  search,
  status,
  onSearchChange,
  onStatusChange,
  filteredCount,
  totalCount,
}) => (
  <Box
    sx={{
      px: 0.85,
      py: 0.7,
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1.55fr) minmax(124px, 0.62fr) auto" },
      gap: 0.55,
      alignItems: "center",
      borderBottom: `1px solid ${BORDER_COLORS.LIGHT}`,
      backgroundColor: "rgba(23, 64, 69, 0.02)",
    }}>
    <TextField
      size="small"
      placeholder="Buscar proveedor, pedido o usuario..."
      value={search}
      onChange={(event) => onSearchChange(event.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ color: "rgba(0,0,0,0.45)", fontSize: 16 }} />
          </InputAdornment>
        ),
        sx: {
          height: 38,
          fontSize: "0.78rem",
          backgroundColor: "#fff",
        },
      }}
      sx={{
        minWidth: 0,
        "& .MuiInputBase-input": {
          py: 0.7,
        },
      }}
    />
    <TextField
      select
      size="small"
      value={status}
      onChange={(event) => onStatusChange(event.target.value)}
      sx={{
        minWidth: 0,
        "& .MuiInputBase-root": {
          height: 38,
          fontSize: "0.78rem",
          backgroundColor: "#fff",
        },
      }}>
      <MenuItem value="all">Todos los estados</MenuItem>
      <MenuItem value="pendiente">Pendiente</MenuItem>
      <MenuItem value="parcial">Parcial</MenuItem>
      <MenuItem value="recibido">Recibido</MenuItem>
      <MenuItem value="incidencia">Incidencia</MenuItem>
    </TextField>
    <Typography sx={{ fontSize: "0.68rem", color: "text.secondary", textAlign: { xs: "left", sm: "right" } }}>
      {filteredCount === totalCount ? `${totalCount} registros` : `${filteredCount} de ${totalCount}`}
    </Typography>
  </Box>
);

const splitHistoryNote = (nota) => {
  const raw = String(nota ?? "").trim();
  if (!raw) {
    return {
      groupComment: "",
      lineComment: "",
    };
  }

  const separatorIndex = raw.indexOf("|");
  if (separatorIndex === -1) {
    return {
      groupComment: "",
      lineComment: raw,
    };
  }

  return {
    groupComment: raw.slice(0, separatorIndex).trim(),
    lineComment: raw.slice(separatorIndex + 1).trim(),
  };
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const buildEmailPlainTextFromHtml = (html) =>
  String(html ?? "")
    .replace(/<li>/g, "\n- ")
    .replace(/<\/li>/g, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&rarr;/g, "→")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const buildForniturasProveedorEmailBody = (proveedorNombre, lines, groupNote = "") => {
  let greetingName = String(proveedorNombre ?? "").trim();
  if (!greetingName) {
    greetingName = "proveedor";
  }

  let itemsHtml = "";
  (Array.isArray(lines) ? lines : []).forEach((ln) => {
    let cantidad = String(ln?.cantidad ?? "").trim().replace(",", ".");
    if (cantidad !== "" && !Number.isNaN(Number(cantidad))) {
      if (cantidad.includes(".")) {
        cantidad = cantidad.replace(/0+$/, "").replace(/\.$/, "");
      }
    }

    const unidad = String(ln?.unidad ?? "").trim();
    const ref = String(ln?.referenciaprov ?? "").trim();
    const nota = String(ln?.nota ?? "").trim();

    let line = escapeHtml(`${cantidad} ${unidad}`.trim());
    if (ref) {
      line += ` &rarr; ${escapeHtml(ref)}`;
    }
    if (nota) {
      line += ` — ${escapeHtml(nota)}`;
    }
    itemsHtml += `<li>${line}</li>`;
  });

  const safeGroupNote = String(groupNote ?? "").trim();
  const groupHtml = safeGroupNote ? `<p>${escapeHtml(safeGroupNote)}</p>` : "";

  return `
        <div style="font-family: Arial, sans-serif; color: #222;">
          <p>Buenos días ${escapeHtml(greetingName)},</p>
          <p>Tenemos un pedido nuevo de:</p>
          <ul>
            ${itemsHtml}
          </ul>
          ${groupHtml}
          <p>Gracias,</p>
        </div>
    `.trim();
};

const buildForniturasProveedorEmailBodyModificacion = ({
  proveedorNombre,
  batchUid,
  pedidoId,
  codigo,
  referenciaProv,
  unidad,
  cantidadOld,
  cantidadNew,
}) => {
  let greetingName = String(proveedorNombre ?? "").trim();
  if (!greetingName) {
    greetingName = "proveedor";
  }

  const safeCodigo = String(codigo ?? "").trim();
  const safeReferenciaProv = String(referenciaProv ?? "").trim();
  const safeUnidad = String(unidad ?? "").trim();
  const oldS = formatEmailQuantityValue(cantidadOld);
  const newS = formatEmailQuantityValue(cantidadNew);

  return `
        <div style="font-family: Arial, sans-serif; color: #222;">
          <p>Buenos días ${escapeHtml(greetingName)},</p>
          <p>Modificación del pedido de fornituras:</p>
          <ul>
            <li><b>Pedido</b>: ${escapeHtml(String(pedidoId ?? ""))} (${escapeHtml(String(batchUid ?? ""))})</li>
            <li><b>Código</b>: ${escapeHtml(safeCodigo)}</li>
            <li><b>Ref. prov</b>: ${escapeHtml(safeReferenciaProv)}</li>
            <li><b>Cantidad</b>: ${escapeHtml(`${oldS} ${safeUnidad}`.trim())} &rarr; <b>${escapeHtml(`${newS} ${safeUnidad}`.trim())}</b></li>
          </ul>
          <p>Gracias,</p>
        </div>
    `.trim();
};

const SidePanel = ({ title, action, children, sx, contentSx }) => (
  <Paper elevation={1} sx={{ ...sidePanelPaperSx, ...(sx || {}) }}>
    <Box sx={sidePanelHeaderSx}>
      <Typography variant="subtitle2" fontWeight={600}>
        {title}
      </Typography>
      {action || null}
    </Box>
    <Box sx={{ p: 0, minHeight: 0, ...(contentSx || {}) }}>{children}</Box>
  </Paper>
);

const PanelDialog = ({ open, title, onClose, children, maxWidth = "lg" }) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth={maxWidth}
    PaperProps={{
      sx: {
        width: "110%",
        maxWidth: "110%",
      },
    }}>
    <DialogTitle
      sx={{
        pb: 1,
        pr: 6,
        display: "flex",
        alignItems: "center",
      }}>
      {title}
      <IconButton
        aria-label="Cerrar"
        onClick={onClose}
        size="small"
        sx={{
          position: "absolute",
          right: 12,
          top: 12,
          color: "text.secondary",
        }}>
        <Close sx={{ fontSize: 18 }} />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers sx={{ p: 2 }}>
      {children}
    </DialogContent>
  </Dialog>
);

const createEmptyPartialReceiveDialogState = (defaultUser = "") => ({
  open: false,
  batchUid: "",
  pedidoId: null,
  lineItem: null,
  cantidad: "",
  usuario: String(defaultUser ?? "").trim(),
  nota: "",
});

const createEmptyEditQuantityDialogState = () => ({
  open: false,
  batchUid: "",
  pedidoId: null,
  lineItem: null,
  cantidad: "",
});

const OrderHistoryTable = ({
  rows,
  maxHeight = { xs: 280, xl: 340 },
  tableSx,
  framed = true,
  onRowClick,
  onConfirmReceive,
  confirmingBatchUid,
}) => (
  <TableContainer
    sx={{
      maxHeight,
      overflowY: "auto",
      overflowX: "hidden",
      ...(framed
        ? {
            border: `1px solid ${BORDER_COLORS.LIGHT}`,
            borderRadius: 1,
          }
        : {}),
      ...(tableSx || {}),
    }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell sx={{ ...compactHeadSx, width: "13%" }}>Fecha</TableCell>
          <TableCell sx={{ ...compactHeadSx, width: "17%" }}>Proveedor</TableCell>
          <TableCell sx={{ ...compactHeadSx, width: "31%" }}>Pedido</TableCell>
          <TableCell sx={{ ...compactHeadSx, width: "12%" }}>Usuario</TableCell>
          <TableCell sx={{ ...compactHeadSx, width: "13%" }}>Estado</TableCell>
          <TableCell sx={{ ...compactHeadSx, width: "14%" }}>Recepción</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
              <Typography color="text.secondary" variant="body2">
                No hay pedidos todavía.
              </Typography>
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row) => {
            const fechaParts = formatHistoryDateParts(row.fecha);
            return (
            <TableRow
              key={row.id}
              hover
              onClick={() => onRowClick?.(row)}
              sx={{ cursor: onRowClick ? "pointer" : "default" }}>
              <TableCell sx={historyCellSx}>
                <Box sx={{ display: "grid", gap: 0.05 }}>
                  <Typography sx={{ fontSize: "0.78rem", lineHeight: 1.2 }}>
                    {fechaParts.date}
                  </Typography>
                  {fechaParts.time ? (
                    <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", lineHeight: 1.15 }}>
                      {fechaParts.time}
                    </Typography>
                  ) : null}
                </Box>
              </TableCell>
              <TableCell sx={historyCellSx}>{row.proveedor || "—"}</TableCell>
              <TableCell sx={{ ...historyCellSx, whiteSpace: "normal", wordBreak: "break-word" }}>
                {row.pedido || "—"}
              </TableCell>
              <TableCell sx={historyCellSx}>{row.usuario || "—"}</TableCell>
              <TableCell sx={historyCellSx}>
                <HistoryStatusBadge status={row.estado} />
              </TableCell>
              <TableCell sx={historyCellSx}>
                {!row.recibidoFecha ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={!row.batch_uid || confirmingBatchUid === row.batch_uid}
                    onClick={(event) => {
                      event.stopPropagation();
                      onConfirmReceive?.(row);
                    }}
                    sx={{
                      minWidth: 0,
                      px: 0.85,
                      py: 0.15,
                      fontSize: "0.72rem",
                      textTransform: "none",
                      whiteSpace: "nowrap",
                    }}>
                    {confirmingBatchUid === row.batch_uid ? "Confirmando..." : "Confirmar"}
                  </Button>
                ) : (
                  <Typography sx={{ fontSize: "0.66rem", color: "text.secondary", whiteSpace: "nowrap" }}>
                    {formatHistoryDate(row.recibidoFecha)}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          )})
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

const OrderHistoryDetailContent = ({
  order,
  loading,
  onConfirmReceiveProvider,
  confirmingProviderKey,
  onConfirmReceiveLine,
  confirmingLineKey,
  onOpenPartialReceiveLine,
  onOpenEditLineQuantity,
  editingLineKey,
  onPreviewEmail,
}) => {
  if (!order && loading) {
    return (
      <Box sx={{ display: "grid", gap: 1.25 }}>
        <Skeleton variant="rounded" height={42} />
        <Skeleton variant="rounded" height={42} />
        <Skeleton variant="rounded" height={220} />
      </Box>
    );
  }

  if (!order) {
    return <Alert severity="info">No se ha podido cargar el detalle del pedido.</Alert>;
  }

  const parsedItems = (order.items || []).map((item) => ({
    ...item,
    parsedNote: splitHistoryNote(item.nota),
  }));
  const groupComment =
    parsedItems.find((item) => item.parsedNote.groupComment)?.parsedNote.groupComment || "";
  const providers = Array.isArray(order.proveedores) ? order.proveedores : [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(5, minmax(0, 1fr))" },
          gap: 1,
        }}>
        <Paper variant="outlined" sx={{ p: 1.25 }}>
          <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>Pedido ID</Typography>
          <Typography sx={{ fontSize: "0.88rem", fontWeight: 700 }}>{order.pedido_id || "—"}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.25 }}>
          <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>Batch UID</Typography>
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 700 }}>{order.batch_uid || "—"}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.25 }}>
          <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>Usuario</Typography>
          <Typography sx={{ fontSize: "0.88rem", fontWeight: 700 }}>{order.usuario || "—"}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.25 }}>
          <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>Fecha alta</Typography>
          <Typography sx={{ fontSize: "0.88rem", fontWeight: 700 }}>{formatHistoryDate(order.fecha)}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.25 }}>
          <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>Recibido</Typography>
          <Typography sx={{ fontSize: "0.88rem", fontWeight: 700 }}>
            {formatHistoryDate(order.recibidoFecha)}
          </Typography>
        </Paper>
      </Box>

      {loading ? (
        <Typography sx={{ fontSize: "0.76rem", color: "text.secondary" }}>
          Actualizando detalle...
        </Typography>
      ) : null}

      {providers.length > 0 ? (
        <Paper variant="outlined" sx={{ p: 0.8 }}>
          <Typography sx={{ fontSize: "0.68rem", color: "text.secondary", mb: 0.45 }}>
            Recepción por proveedor
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(auto-fit, minmax(250px, 420px))",             
              },
              gap: 1.5,
              justifyContent: "flex-start",
            }}>
            {providers.map((provider) => {
              const proveedorNombre = String(provider?.proveedor ?? "").trim();
              const providerConfirmKey = `${order.batch_uid}::${proveedorNombre}`;
              const isReceived = Boolean(provider?.recibidoFecha);
              const isConfirming = confirmingProviderKey === providerConfirmKey;
              const providerLines = parsedItems.filter(
                (item) => getHistoryItemProviderName(item) === proveedorNombre
              );
              const providerGroupComment =
                providerLines.find((item) => item?.parsedNote?.groupComment)?.parsedNote?.groupComment || "";

              return (
                <Paper
                  key={providerConfirmKey}
                  variant="outlined"
                  sx={{
                    p: 0.55,
                    width: "100%",
                    borderColor: isReceived ? "rgba(46, 125, 50, 0.28)" : "rgba(25, 118, 210, 0.18)",
                    backgroundColor: isReceived ? "rgba(46, 125, 50, 0.04)" : "#fff",
                  }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.75 }}>
                    <Box sx={{ minWidth: 0, flex: 1, display: "flex", alignItems: "center", gap: 0.6 }}>
                      <Typography
                        sx={{
                          fontSize: "0.76rem",
                          fontWeight: 700,
                          color: BRAND_COLORS.PRIMARY_DARK || "#174045",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}>
                        {proveedorNombre || "—"}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.68rem",
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}>
                        {isReceived ? `Recibido: ${formatHistoryDate(provider?.recibidoFecha)}` : "Pendiente de recepción"}
                      </Typography>
                      <Tooltip title="Ver correo enviado al proveedor">
                        <IconButton
                          size="small"
                          onClick={() =>
                            onPreviewEmail?.({
                              title: `Correo ${proveedorNombre || "proveedor"}`,
                              html: buildForniturasProveedorEmailBody(
                                proveedorNombre,
                                providerLines.map((line) => ({
                                  cantidad: line.cantidad,
                                  unidad: line.unidad,
                                  referenciaprov: line.referenciaprov,
                                  nota: line.parsedNote?.lineComment || "",
                                })),
                                providerGroupComment
                              ),
                            })
                          }
                          sx={{ color: "rgba(23, 64, 69, 0.72)", p: 0.3, flexShrink: 0 }}>
                          <MailOutline sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Button
                      size="small"
                      variant={isReceived ? "outlined" : "contained"}
                      color={isReceived ? "success" : "primary"}
                      disabled={!proveedorNombre || isReceived || isConfirming}
                      onClick={() => onConfirmReceiveProvider?.(order, proveedorNombre)}
                      sx={{
                        minWidth: 0,
                        minHeight: 26,
                        textTransform: "none",
                        whiteSpace: "nowrap",
                        px: 0.75,
                        py: 0.2,
                        fontSize: "0.68rem",
                        lineHeight: 1.2,
                      }}>
                      {isReceived ? "Recibido" : isConfirming ? "Confirmando..." : "Confirmar"}
                    </Button>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Paper>
      ) : null}

      <TableContainer sx={{ border: `1px solid ${BORDER_COLORS.LIGHT}`, borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={compactHeadSx}>Línea</TableCell>
              <TableCell sx={compactHeadSx}>Código</TableCell>
              <TableCell sx={compactHeadSx}>Nombre</TableCell>
              <TableCell sx={compactHeadSx}>Referencia prov.</TableCell>
              <TableCell sx={compactHeadSx}>Pedida</TableCell>
              <TableCell sx={compactHeadSx}>Recepcionada</TableCell>
              <TableCell sx={compactHeadSx}>Pendiente</TableCell>
              <TableCell sx={compactHeadSx}>Unidad</TableCell>
              <TableCell sx={compactHeadSx}>Nota</TableCell>
              <TableCell sx={compactHeadSx}>Acción</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parsedItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary" variant="body2">
                    Este pedido no tiene líneas.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              parsedItems.map((item) => {
                const lineConfirmKey = getLineMutationKey(order.batch_uid, item);
                const isLineReceived = Boolean(item?.recibidoFecha);
                const isLineConfirming = confirmingLineKey === lineConfirmKey;
                const isLineEditing = editingLineKey === lineConfirmKey;
                const receivedQty = getLineReceivedTotal(item);
                const pendingQty = getLinePendingQuantity(item);
                const canConfirmTotal =
                  Boolean(item?.linea_id) && !isLineReceived && !isLineConfirming && pendingQty > 0;
                const canConfirmPartial = canConfirmTotal && !isLineEditing;
                const canEditQuantity =
                  Boolean(item?.linea_id) &&
                  !isLineReceived &&
                  !isLineEditing &&
                  !isLineConfirming &&
                  receivedQty <= 0;

                return (
                <TableRow key={item.linea_id ?? `${order.id}-${item.linea_n}-${item.codigo}`}>
                  <TableCell sx={detailLineCellSx}>{item.linea_n || "—"}</TableCell>
                  <TableCell sx={detailLineCellSx}>{item.codigo || "—"}</TableCell>
                  <TableCell sx={detailLineCellSx}>{item.nombre || "—"}</TableCell>
                  <TableCell sx={detailLineCellSx}>{item.referenciaprov || "—"}</TableCell>
                  <TableCell sx={detailLineCellSx}>{formatQuantityValue(item.cantidad)}</TableCell>
                  <TableCell sx={detailLineCellSx}>{formatQuantityValue(receivedQty)}</TableCell>
                  <TableCell sx={detailLineCellSx}>{formatQuantityValue(pendingQty)}</TableCell>
                  <TableCell sx={detailLineCellSx}>{item.unidad || "—"}</TableCell>
                  <TableCell sx={{ ...detailLineCellSx, whiteSpace: "normal", wordBreak: "break-word" }}>
                    {item.parsedNote.lineComment || "—"}
                  </TableCell>
                  <TableCell sx={detailLineCellSx}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
                      <Button
                        size="small"
                        variant={isLineReceived ? "outlined" : "contained"}
                        color={isLineReceived ? "success" : "primary"}
                        disabled={!canConfirmTotal}
                        onClick={() => onConfirmReceiveLine?.(order, item)}
                        sx={detailLineActionButtonSx}>
                        {isLineReceived ? "Recibida" : isLineConfirming ? "Confirmando..." : "Confirmar"}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!canConfirmPartial}
                        onClick={() => onOpenPartialReceiveLine?.(order, item)}
                        sx={detailLineActionButtonSx}>
                        Parcial
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!canEditQuantity}
                        onClick={() => onOpenEditLineQuantity?.(order, item)}
                        sx={detailLineActionButtonSx}>
                        Editar cantidad
                      </Button>
                    </Box>
                    {item.recibidoFecha ? (
                      <Typography sx={{ mt: 0.45, fontSize: "0.68rem", color: "text.secondary" }}>
                        Recibida: {formatHistoryDate(item.recibidoFecha)}
                      </Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
              )})
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {groupComment ? (
        <Paper variant="outlined" sx={{ p: 1.25 }}>
          <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", mb: 0.35 }}>
            Comentario de grupo
          </Typography>
          <Typography sx={{ fontSize: "0.84rem", fontWeight: 700, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {groupComment}
          </Typography>
        </Paper>
      ) : null}
    </Box>
  );
};

const OrderDraftItemCard = memo(function OrderDraftItemCard({
  item,
  availableProviderOptions,
  onUpdateItem,
  onRemoveItem,
  onStepQuantity,
}) {
  const [noteDraft, setNoteDraft] = useState(item.nota || "");
  const [refProvDraft, setRefProvDraft] = useState(item.referenciaprov || "");
  const [isEditingRefProv, setIsEditingRefProv] = useState(false);
  const selectableProviders = useMemo(
    () => mergeProviderOptions(item.providerOptions, availableProviderOptions),
    [item.providerOptions, availableProviderOptions]
  );
  const selectedProviderKey = item.providerKey || selectableProviders[0]?.key || "";
  const hasReferenciaProvCommitted = Boolean(String(item.referenciaprov || "").trim());
  const hasReferenciaProvDraft = Boolean(String(refProvDraft || "").trim());
  const shouldShowRefProvInput = isEditingRefProv || !hasReferenciaProvCommitted;

  useEffect(() => {
    setNoteDraft(item.nota || "");
  }, [item.codigo, item.nota]);

  useEffect(() => {
    if (isEditingRefProv) return;
    setRefProvDraft(item.referenciaprov || "");
  }, [item.codigo, item.referenciaprov, isEditingRefProv]);

  useEffect(() => {
    if (noteDraft === (item.nota || "")) return undefined;
    const timeoutId = setTimeout(() => {
      onUpdateItem(item.codigo, { nota: noteDraft });
    }, 180);

    return () => clearTimeout(timeoutId);
  }, [noteDraft, item.codigo, item.nota, onUpdateItem]);

  useEffect(() => {
    if (refProvDraft === (item.referenciaprov || "")) return undefined;
    const timeoutId = setTimeout(() => {
      onUpdateItem(item.codigo, { referenciaprov: refProvDraft });
    }, 180);

    return () => clearTimeout(timeoutId);
  }, [refProvDraft, item.codigo, item.referenciaprov, onUpdateItem]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.7,
        p: 0.8,
        borderRadius: 1.2,
        backgroundColor: "#fff",
        border: hasReferenciaProvDraft ? "1px solid rgba(23, 64, 69, 0.18)" : "1px solid rgba(211, 47, 47, 0.35)",
        boxShadow: "0 6px 14px rgba(23, 64, 69, 0.08)",
      }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.8, mb: 0.2 }}>
            <Typography
              sx={{
                fontSize: "0.62rem",
                fontWeight: 800,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: "rgba(23, 64, 69, 0.68)",
              }}>
              Referencia individual
            </Typography>
            <Box
              sx={{
                px: 0.55,
                py: 0.12,
                borderRadius: 999,
                backgroundColor: "rgba(23, 64, 69, 0.07)",
                border: "1px solid rgba(23, 64, 69, 0.12)",
              }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  fontWeight: 800,
                  color: "rgba(23, 64, 69, 0.78)",
                  textTransform: "uppercase",
                  lineHeight: 1.2,
                }}>
                1 ref
              </Typography>
            </Box>
          </Box>
          <Typography
            sx={{
              fontSize: "0.92rem",
              fontWeight: 700,
              color: BRAND_COLORS.PRIMARY_DARK || "#174045",
              lineHeight: 1.15,
            }}>
            {item.codigo}
          </Typography>
          {selectableProviders.length > 0 ? (
            <TextField
              select
              size="small"
              value={selectedProviderKey}
              onChange={(e) => onUpdateItem(item.codigo, { providerKey: e.target.value })}
              sx={{ ...orderCompactFieldSx, mt: 0.35, minWidth: 0, width: "100%" }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) =>
                  selectableProviders.find((provider) => provider.key === selected)?.nombre ||
                  "Proveedor sin definir",
              }}>
              {selectableProviders.map((provider) => (
                <MenuItem key={provider.key} value={provider.key}>
                  {provider.nombre}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Typography
              sx={{
                fontSize: "0.72rem",
                color: "text.secondary",
                mt: 0.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}>
              Proveedor sin definir
            </Typography>
          )}

          <Box sx={{ mt: 0.4 }}>
            {!shouldShowRefProvInput ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  px: 0.85,
                  py: 0.65,
                  borderRadius: 1.1,
                  border: "1px solid rgba(23, 64, 69, 0.14)",
                  backgroundColor: "rgba(23, 64, 69, 0.04)",
                }}>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: "rgba(0,0,0,0.62)" }}>
                  Ref. prov
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    color: BRAND_COLORS.PRIMARY_DARK || "#174045",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    textAlign: "right",
                  }}>
                  {item.referenciaprov}
                </Typography>
              </Box>
            ) : (
              <TextField
                size="small"
                value={refProvDraft}
                onChange={(e) => setRefProvDraft(e.target.value)}
                placeholder="Añadir referencia prov..."
                error={!hasReferenciaProvDraft}
                helperText={!hasReferenciaProvDraft ? "Falta referencia prov" : " "}
                sx={{
                  ...orderCompactFieldSx,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(211, 47, 47, 0.04)",
                    "& fieldset": {
                      borderColor: "rgba(211, 47, 47, 0.35)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(211, 47, 47, 0.55)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "rgba(211, 47, 47, 0.75)",
                    },
                  },
                  "& .MuiFormHelperText-root": {
                    marginLeft: 0,
                    marginRight: 0,
                    fontSize: "0.68rem",
                    fontWeight: 700,
                  },
                }}
                fullWidth
                onFocus={() => setIsEditingRefProv(true)}
                onBlur={() => {
                  const trimmed = String(refProvDraft || "").trim();
                  if (trimmed !== String(item.referenciaprov || "").trim()) {
                    onUpdateItem(item.codigo, { referenciaprov: trimmed });
                  }
                  setIsEditingRefProv(false);
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 0.7,
          alignItems: "center",
        }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "28px 1fr 28px",
            alignItems: "stretch",
            borderRadius: 1,
            overflow: "hidden",
            border: `1px solid ${BORDER_COLORS.LIGHT}`,
            backgroundColor: "#f3f6f8",
          }}>
          <IconButton size="small" onClick={() => onStepQuantity(item.codigo, -1)} sx={{ borderRadius: 0 }}>
            <Remove sx={{ fontSize: 16 }} />
          </IconButton>
          <TextField
            size="small"
            type="number"
            value={item.cantidad}
            onChange={(e) => onUpdateItem(item.codigo, { cantidad: e.target.value })}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              inputProps: {
                min: 1,
                style: {
                  textAlign: "center",
                  padding: "6px 0",
                  fontSize: "0.8rem",
                  fontWeight: 700,
                },
              },
            }}
            sx={{
              "& .MuiInputBase-root": {
                height: "100%",
                backgroundColor: "#fff",
              },
              "& input[type=number]": {
                MozAppearance: "textfield",
              },
              "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                {
                  WebkitAppearance: "none",
                  margin: 0,
                },
            }}
          />
          <IconButton size="small" onClick={() => onStepQuantity(item.codigo, 1)} sx={{ borderRadius: 0 }}>
            <Add sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <TextField
          select
          size="small"
          value={item.unidad || "Unidades"}
          onChange={(e) => onUpdateItem(item.codigo, { unidad: e.target.value })}
          sx={orderCompactFieldSx}
          fullWidth>
          <MenuItem value="Unidades">Unidades</MenuItem>
          <MenuItem value="Metros">Metros</MenuItem>
        </TextField>
      </Box>

      <TextField
        multiline
        minRows={1}
        maxRows={2}
        placeholder="Escribir nota..."
        value={noteDraft}
        onChange={(e) => setNoteDraft(e.target.value)}
        onBlur={() => {
          if (noteDraft !== (item.nota || "")) {
            onUpdateItem(item.codigo, { nota: noteDraft });
          }
        }}
        sx={orderNoteFieldSx}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteOutline sx={{ fontSize: 15 }} />}
          onClick={() => onRemoveItem(item.codigo)}
          sx={{
            minWidth: 0,
            px: 0.2,
            py: 0.05,
            fontWeight: 600,
            fontSize: "0.72rem",
            textTransform: "none",
          }}>
          Quitar
        </Button>
      </Box>
    </Box>
  );
});

const OrderDraftRefProvField = memo(function OrderDraftRefProvField({ item, onUpdateItem, inline = false }) {
  const [refProvDraft, setRefProvDraft] = useState(item.referenciaprov || "");
  const [isEditingRefProv, setIsEditingRefProv] = useState(false);
  const hasReferenciaProvCommitted = Boolean(String(item.referenciaprov || "").trim());
  const hasReferenciaProvDraft = Boolean(String(refProvDraft || "").trim());
  const shouldShowRefProvInput = isEditingRefProv || !hasReferenciaProvCommitted;

  useEffect(() => {
    if (isEditingRefProv) return;
    setRefProvDraft(item.referenciaprov || "");
  }, [item.codigo, item.referenciaprov, isEditingRefProv]);

  useEffect(() => {
    if (refProvDraft === (item.referenciaprov || "")) return undefined;
    const timeoutId = setTimeout(() => {
      onUpdateItem(item.codigo, { referenciaprov: refProvDraft });
    }, 180);
    return () => clearTimeout(timeoutId);
  }, [refProvDraft, item.codigo, item.referenciaprov, onUpdateItem]);

  return (
    <Box sx={{ mt: inline ? 0 : 0.35 }}>
      {!shouldShowRefProvInput ? (
        <Box
          onClick={() => setIsEditingRefProv(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.7,
            px: inline ? 0.5 : 0.65,
            py: inline ? 0 : 0.45,
            minHeight: inline ? 28 : "auto",
            borderRadius: 0.9,
            border: "1px solid rgba(23, 64, 69, 0.14)",
            backgroundColor: "rgba(23, 64, 69, 0.04)",
            cursor: "text",
          }}>
          {!inline ? (
            <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: "rgba(0,0,0,0.62)" }}>
              Ref. prov
            </Typography>
          ) : null}
          <Typography
            sx={{
              fontSize: "0.74rem",
              fontWeight: 700,
              color: BRAND_COLORS.PRIMARY_DARK || "#174045",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textAlign: "right",
            }}>
            {item.referenciaprov}
          </Typography>
        </Box>
      ) : (
        <TextField
          size="small"
          value={refProvDraft}
          onChange={(e) => setRefProvDraft(e.target.value)}
          placeholder="Añadir referencia prov..."
          error={!hasReferenciaProvDraft}
          helperText={inline ? "" : !hasReferenciaProvDraft ? "Falta referencia prov" : " "}
          sx={{
            ...(inline ? orderSingleLineFieldSx : orderCompactFieldSx),
            "& .MuiOutlinedInput-root": {
              height: inline ? 28 : undefined,
              backgroundColor: "rgba(211, 47, 47, 0.04)",
              "& fieldset": { borderColor: "rgba(211, 47, 47, 0.35)" },
              "&:hover fieldset": { borderColor: "rgba(211, 47, 47, 0.55)" },
              "&.Mui-focused fieldset": { borderColor: "rgba(211, 47, 47, 0.75)" },
            },
            "& .MuiFormHelperText-root": {
              marginLeft: 0,
              marginRight: 0,
              minHeight: inline ? 0 : undefined,
              fontSize: inline ? "0.58rem" : "0.64rem",
              fontWeight: 700,
            },
          }}
          fullWidth
          onFocus={() => setIsEditingRefProv(true)}
          onBlur={() => {
            const trimmed = String(refProvDraft || "").trim();
            if (trimmed !== String(item.referenciaprov || "").trim()) {
              onUpdateItem(item.codigo, { referenciaprov: trimmed });
            }
            setIsEditingRefProv(false);
          }}
        />
      )}
    </Box>
  );
});

const OrderDraftGroupCard = memo(function OrderDraftGroupCard({
  providerKey,
  items,
  availableProviderOptions,
  onUpdateItem,
  onRemoveItem,
  onStepQuantity,
}) {
  const groupProviderName =
    items.find((it) => it.providerKey === providerKey)?.proveedorNombre ||
    items.find((it) => it.providerKey === providerKey)?.proveedor ||
    "Proveedor";

  const initialNote = useMemo(() => {
    const notes = items.map((it) => String(it.nota || ""));
    const first = notes[0] ?? "";
    const allEqual = notes.every((n) => n === first);
    return allEqual ? first : "";
  }, [items]);

  const [groupNoteDraft, setGroupNoteDraft] = useState(initialNote);

  useEffect(() => {
    setGroupNoteDraft(initialNote);
  }, [initialNote, providerKey]);

  useEffect(() => {
    const targetItems = items.filter((it) => it.providerKey === providerKey);
    if (targetItems.length === 0) return undefined;
    const allAlreadyMatch = targetItems.every((it) => String(it.nota || "") === groupNoteDraft);
    if (allAlreadyMatch) return undefined;
    const timeoutId = setTimeout(() => {
      targetItems.forEach((it) => onUpdateItem(it.codigo, { nota: groupNoteDraft }));
    }, 220);
    return () => clearTimeout(timeoutId);
  }, [groupNoteDraft, items, onUpdateItem, providerKey]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.8,
        p: 0.8,
        borderRadius: 1.2,
        backgroundColor: "#fff",
        border: "1px solid rgba(43, 108, 176, 0.22)",
        borderLeft: "4px solid rgba(43, 108, 176, 0.75)",
        boxShadow: "0 6px 14px rgba(23, 64, 69, 0.08)",
      }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 0.7,
          py: 0.45,
          borderRadius: 0.9,
          backgroundColor: "rgba(43, 108, 176, 0.08)",
          border: "1px solid rgba(43, 108, 176, 0.16)",
        }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "0.6rem",
              fontWeight: 800,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "rgba(43, 108, 176, 0.86)",
              mb: 0.05,
            }}>
            Grupo por proveedor
          </Typography>
          <Typography
            sx={{
              fontSize: "0.78rem",
              fontWeight: 800,
              color: BRAND_COLORS.PRIMARY_DARK || "#174045",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}>
            {groupProviderName}
          </Typography>
        </Box>
        <Box
          sx={{
            px: 0.6,
            py: 0.18,
            borderRadius: 999,
            backgroundColor: "rgba(43, 108, 176, 0.14)",
            border: "1px solid rgba(43, 108, 176, 0.18)",
            flexShrink: 0,
          }}>
          <Typography sx={{ fontSize: "0.64rem", color: "rgba(43, 108, 176, 0.92)", fontWeight: 800 }}>
            {items.length} refs
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.7 }}>
        {items.map((item, index) => (
          <Box
            key={item.codigo}
            sx={{
              borderRadius: 1,
              border: "1px solid rgba(43, 108, 176, 0.10)",
              backgroundColor: "rgba(248, 251, 255, 0.98)",
              p: 0.75,
              display: "flex",
              flexDirection: "column",
              gap: 0.65,
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.55)",
              ...(index > 0
                ? {
                    borderTop: "1px dashed rgba(43, 108, 176, 0.18)",
                  }
                : {}),
            }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 0.45 }}>
              <Typography
                sx={{
                  fontSize: "0.82rem",
                  fontWeight: 800,
                  color: BRAND_COLORS.PRIMARY_DARK || "#174045",
                  lineHeight: 1.1,
                }}>
                {item.codigo}
              </Typography>

              <TextField
                select
                size="small"
                value={item.providerKey || ""}
                onChange={(e) => onUpdateItem(item.codigo, { providerKey: e.target.value })}
                sx={{ ...orderCompactFieldSx, minWidth: 0, width: "100%" }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) =>
                    mergeProviderOptions(item.providerOptions, availableProviderOptions).find((p) => p.key === selected)
                      ?.nombre || "Proveedor sin definir",
                }}>
                {mergeProviderOptions(item.providerOptions, availableProviderOptions).map((provider) => (
                  <MenuItem key={provider.key} value={provider.key}>
                    {provider.nombre}
                  </MenuItem>
                ))}
              </TextField>

              <OrderDraftRefProvField item={item} onUpdateItem={onUpdateItem} />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 0.7,
                alignItems: "center",
              }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "28px 1fr 28px",
                  alignItems: "stretch",
                  borderRadius: 1,
                  overflow: "hidden",
                  border: `1px solid ${BORDER_COLORS.LIGHT}`,
                  backgroundColor: "#f3f6f8",
                }}>
                <IconButton size="small" onClick={() => onStepQuantity(item.codigo, -1)} sx={{ borderRadius: 0 }}>
                  <Remove sx={{ fontSize: 16 }} />
                </IconButton>
                <TextField
                  size="small"
                  type="number"
                  value={item.cantidad}
                  onChange={(e) => onUpdateItem(item.codigo, { cantidad: e.target.value })}
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    inputProps: {
                      min: 1,
                      style: {
                        textAlign: "center",
                        padding: "6px 0",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                      },
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-root": { height: "100%", backgroundColor: "#fff" },
                    "& input[type=number]": { MozAppearance: "textfield" },
                    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                  }}
                />
                <IconButton size="small" onClick={() => onStepQuantity(item.codigo, 1)} sx={{ borderRadius: 0 }}>
                  <Add sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>

              <TextField
                select
                size="small"
                value={item.unidad || "Unidades"}
                onChange={(e) => onUpdateItem(item.codigo, { unidad: e.target.value })}
                sx={orderCompactFieldSx}
                fullWidth>
                <MenuItem value="Unidades">Unidades</MenuItem>
                <MenuItem value="Metros">Metros</MenuItem>
              </TextField>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                size="small"
                color="error"
                startIcon={<DeleteOutline sx={{ fontSize: 15 }} />}
                onClick={() => onRemoveItem(item.codigo)}
                sx={{
                  minWidth: 0,
                  px: 0.2,
                  py: 0.05,
                  fontWeight: 600,
                  fontSize: "0.72rem",
                  textTransform: "none",
                }}>
                Quitar
              </Button>
            </Box>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          pt: 0.15,
          borderTop: "1px solid rgba(43, 108, 176, 0.12)",
        }}>
        <Typography
          sx={{
            fontSize: "0.62rem",
            fontWeight: 800,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: "rgba(43, 108, 176, 0.8)",
            mb: 0.35,
          }}>
          Nota compartida del grupo
        </Typography>
        <TextField
          multiline
          minRows={1}
          maxRows={2}
          placeholder="Escribir nota (común para este proveedor)..."
          value={groupNoteDraft}
          onChange={(e) => setGroupNoteDraft(e.target.value)}
          sx={orderNoteFieldSx}
        />
      </Box>
    </Box>
  );
});

const OrderDraftGroupedList = ({
  items,
  availableProviderOptions,
  onUpdateItem,
  onRemoveItem,
  onStepQuantity,
  onPreviewEmail,
}) => {
  const GROUP_STYLES = [
    {
      headerBg: "rgba(43, 108, 176, 0.08)",
      headerBorder: "rgba(43, 108, 176, 0.16)",
      accent: "rgba(43, 108, 176, 0.75)",
      accentSoft: "rgba(43, 108, 176, 0.14)",
      label: "rgba(43, 108, 176, 0.86)",
      rowBg: "rgba(248, 251, 255, 0.98)",
    },
    {
      headerBg: "rgba(46, 125, 50, 0.08)",
      headerBorder: "rgba(46, 125, 50, 0.16)",
      accent: "rgba(46, 125, 50, 0.72)",
      accentSoft: "rgba(46, 125, 50, 0.14)",
      label: "rgba(46, 125, 50, 0.88)",
      rowBg: "rgba(248, 252, 248, 0.98)",
    },
  ];

  if (items.length === 0) {
    return (
      <Box
        sx={{
          minHeight: 220,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 1.4,
          border: `1px dashed ${BORDER_COLORS.LIGHT}`,
          backgroundColor: "rgba(255,255,255,0.82)",
        }}>
        <Typography variant="body2" color="text.secondary">
          Añade códigos desde panel izquierdo.
        </Typography>
      </Box>
    );
  }

  const grouped = items.reduce((acc, item) => {
    const key = item.providerKey || "";
    if (!acc.has(key)) acc.set(key, []);
    acc.get(key).push(item);
    return acc;
  }, new Map());

  const entries = Array.from(grouped.entries()).map(([providerKey, groupItems]) => ({
    providerKey,
    items: groupItems,
  }));
  const sortedEntries = [...entries].sort((a, b) => {
    const aIsGrouped = Boolean(a.providerKey && a.items.length > 1);
    const bIsGrouped = Boolean(b.providerKey && b.items.length > 1);
    if (aIsGrouped === bIsGrouped) return 0;
    return aIsGrouped ? 1 : -1;
  });

  return (
    <TableContainer
      sx={{
        borderRadius: 1.2,
        border: `1px solid ${BORDER_COLORS.LIGHT}`,
        backgroundColor: "#fff",
        overflowX: "auto",
      }}>
      <Table
        size="small"
        sx={{
          tableLayout: "fixed",
          minWidth: 980,
          borderCollapse: "separate",
          borderSpacing: "0 5px",
          "& .MuiTableCell-root": {
            borderBottom: "none",
            verticalAlign: "top",
          },
        }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ py: 0.5, px: 0.45, width: "14%", fontSize: "0.64rem", fontWeight: 800, textTransform: "uppercase", color: "rgba(23, 64, 69, 0.78)", backgroundColor: "#f7fafc" }}>
              Referencia
            </TableCell>
            <TableCell sx={{ py: 0.5, px: 0.45, width: "17%", fontSize: "0.64rem", fontWeight: 800, textTransform: "uppercase", color: "rgba(23, 64, 69, 0.78)", backgroundColor: "#f7fafc" }}>
              Proveedor
            </TableCell>
            <TableCell sx={{ py: 0.5, px: 0.45, width: "17%", fontSize: "0.64rem", fontWeight: 800, textTransform: "uppercase", color: "rgba(23, 64, 69, 0.78)", backgroundColor: "#f7fafc" }}>
              Ref. prov
            </TableCell>
            <TableCell sx={{ py: 0.5, px: 0.45, width: "13%", fontSize: "0.64rem", fontWeight: 800, textTransform: "uppercase", color: "rgba(23, 64, 69, 0.78)", backgroundColor: "#f7fafc" }}>
              Cantidad
            </TableCell>
            <TableCell sx={{ py: 0.5, px: 0.45, width: "12%", fontSize: "0.64rem", fontWeight: 800, textTransform: "uppercase", color: "rgba(23, 64, 69, 0.78)", backgroundColor: "#f7fafc" }}>
              Unidad
            </TableCell>
            <TableCell sx={{ py: 0.5, px: 0.45, width: "21%", fontSize: "0.64rem", fontWeight: 800, textTransform: "uppercase", color: "rgba(23, 64, 69, 0.78)", backgroundColor: "#f7fafc" }}>
              Comentario
            </TableCell>
            <TableCell sx={{ py: 0.5, px: 0.45, width: "6%", fontSize: "0.64rem", fontWeight: 800, textTransform: "uppercase", color: "rgba(23, 64, 69, 0.78)", backgroundColor: "#f7fafc" }}>
              Acción
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedEntries.map((entry, entryIndex) => {
            const shouldGroup = entry.providerKey && entry.items.length > 1;
            const previousEntry = entryIndex > 0 ? sortedEntries[entryIndex - 1] : null;
            const previousWasIndividual = Boolean(
              previousEntry && !(previousEntry.providerKey && previousEntry.items.length > 1)
            );
            const groupProviderName =
              entry.items.find((it) => it.providerKey === entry.providerKey)?.proveedorNombre ||
              entry.items.find((it) => it.providerKey === entry.providerKey)?.proveedor ||
              "Proveedor";
            const groupStyle = GROUP_STYLES[entryIndex % GROUP_STYLES.length];
            const groupNoteValue = (() => {
              const notes = entry.items.map((it) => String(it.notaGrupo || ""));
              const first = notes[0] ?? "";
              return notes.every((n) => n === first) ? first : "";
            })();

            return (
              <React.Fragment key={`entry::${entry.providerKey || entry.items[0]?.codigo}`}>
                {shouldGroup && previousWasIndividual ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{
                        py: 0,
                        px: 0,
                        height: 12,
                        backgroundColor: "transparent",
                        borderBottom: "none",
                      }}
                    />
                  </TableRow>
                ) : null}
                {shouldGroup ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{
                        py: 0.35,
                        px: 0.65,
                        backgroundColor: groupStyle.headerBg,
                        borderLeft: `4px solid ${groupStyle.accent}`,
                        borderBottom: `1px solid ${groupStyle.headerBorder}`,
                      }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                        <Box
                          sx={{
                            minWidth: 0,
                            width: { xs: "100%", md: "13.3%" },
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 0.4,
                          }}>
                          <Box sx={{ minWidth: 0, m: "5px" }}>
                            <Typography sx={{ fontSize: "0.56rem", fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: groupStyle.label, lineHeight: 1.2 }}>
                              Grupo por proveedor
                            </Typography>
                            <Typography sx={{ fontSize: "0.76rem", fontWeight: 800, color: BRAND_COLORS.PRIMARY_DARK || "#174045", lineHeight: 1.15 }}>
                              {groupProviderName}
                            </Typography>
                          </Box>
                          <Tooltip title="Ver correo del proveedor">
                            <IconButton
                              size="small"
                              onClick={() =>
                                onPreviewEmail?.({
                                  title: `Correo ${groupProviderName}`,
                                  html: buildForniturasProveedorEmailBody(
                                    groupProviderName,
                                    entry.items.map((entryItem) => ({
                                      cantidad: entryItem.cantidad,
                                      unidad: entryItem.unidad,
                                      referenciaprov: entryItem.referenciaprov,
                                      nota: entryItem.nota,
                                    })),
                                    groupNoteValue
                                  ),
                                })
                              }
                              sx={{ color: groupStyle.label, p: 0.4, m: "5px", flexShrink: 0, mt: -0.1 }}>
                              <MailOutline sx={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, p: "5px", flexShrink: 0 }}>
                          <Box
                            sx={{
                              p: "3px",
                              borderRadius: 999,
                              backgroundColor: groupStyle.accentSoft,
                              border: `1px solid ${groupStyle.headerBorder}`,
                            }}>
                            <Typography sx={{ fontSize: "0.725rem", color: groupStyle.label, fontWeight: 800 }}>
                              {entry.items.length} refs
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : null}

                {entry.items.map((item, index) => {
                  const selectableProviders = mergeProviderOptions(item.providerOptions, availableProviderOptions);
                  const selectedProviderKey = item.providerKey || selectableProviders[0]?.key || "";
                  const isIndividualEntry = !shouldGroup;

                  return (
                    <TableRow
                      key={item.codigo}
                      sx={{
                        backgroundColor: shouldGroup ? groupStyle.rowBg : "#fff",
                        ...(isIndividualEntry
                          ? {
                              "& .MuiTableCell-root": {
                                borderBottom: "1px solid rgba(23, 64, 69, 0.10)",
                              },
                            }
                          : {}),
                      }}>
                      <TableCell sx={{ py: 0.18, px: 0.42 }}>
                        {!shouldGroup ? (
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.4, mb: 0.08 }}>
                            <Typography sx={{ fontSize: "0.54rem", fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: "rgba(23, 64, 69, 0.62)", lineHeight: 1.1 }}>
                              Individual
                            </Typography>
                            <Tooltip title="Ver correo de la línea">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  onPreviewEmail?.({
                                    title: `Correo ${item.proveedorNombre || item.proveedor || "proveedor"}`,
                                    html: buildForniturasProveedorEmailBody(
                                      item.proveedorNombre || item.proveedor || "",
                                      [
                                        {
                                          cantidad: item.cantidad,
                                          unidad: item.unidad,
                                          referenciaprov: item.referenciaprov,
                                          nota: item.nota,
                                        },
                                      ],
                                      ""
                                    ),
                                  })
                                }
                                sx={{ color: "rgba(23, 64, 69, 0.72)", p: 0.3 }}>
                                <MailOutline sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : null}
                        <Typography sx={{ fontSize: "0.74rem", fontWeight: 800, color: BRAND_COLORS.PRIMARY_DARK || "#174045", lineHeight: 1.1 }}>
                          {item.codigo}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.18, px: 0.42 }}>
                        <TextField
                          select
                          size="small"
                          value={selectedProviderKey}
                          onChange={(e) => onUpdateItem(item.codigo, { providerKey: e.target.value })}
                          sx={{ ...orderCompactFieldSx, minWidth: 0, width: "100%" }}
                          SelectProps={{
                            displayEmpty: true,
                            renderValue: (selected) =>
                              selectableProviders.find((provider) => provider.key === selected)?.nombre ||
                              "Proveedor sin definir",
                          }}>
                          {selectableProviders.map((provider) => (
                            <MenuItem key={provider.key} value={provider.key}>
                              {provider.nombre}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell sx={{ py: 0.18, px: 0.42 }}>
                        <OrderDraftRefProvField item={item} onUpdateItem={onUpdateItem} inline />
                      </TableCell>
                      <TableCell sx={{ py: 0.18, px: 0.42 }}>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "24px 1fr 24px",
                            height: 28,
                            alignItems: "stretch",
                            borderRadius: 1,
                            overflow: "hidden",
                            border: `1px solid ${BORDER_COLORS.LIGHT}`,
                            backgroundColor: "#f3f6f8",
                          }}>
                          <IconButton size="small" onClick={() => onStepQuantity(item.codigo, -1)} sx={{ borderRadius: 0 }}>
                            <Remove sx={{ fontSize: 16 }} />
                          </IconButton>
                          <TextField
                            size="small"
                            type="number"
                            value={item.cantidad}
                            onChange={(e) => onUpdateItem(item.codigo, { cantidad: e.target.value })}
                            variant="standard"
                            InputProps={{
                              disableUnderline: true,
                              inputProps: {
                                min: 1,
                                style: {
                                  textAlign: "center",
                                  padding: "4px 0",
                                  fontSize: "0.74rem",
                                  fontWeight: 700,
                                },
                              },
                            }}
                            sx={{
                              "& .MuiInputBase-root": { height: "100%", backgroundColor: "#fff" },
                              "& input[type=number]": { MozAppearance: "textfield" },
                              "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                                WebkitAppearance: "none",
                                margin: 0,
                              },
                            }}
                          />
                          <IconButton size="small" onClick={() => onStepQuantity(item.codigo, 1)} sx={{ borderRadius: 0 }}>
                            <Add sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.18, px: 0.42 }}>
                        <TextField
                          select
                          size="small"
                          value={item.unidad || "Unidades"}
                          onChange={(e) => onUpdateItem(item.codigo, { unidad: e.target.value })}
                          sx={orderCompactFieldSx}
                          fullWidth>
                          <MenuItem value="Unidades">Unidades</MenuItem>
                          <MenuItem value="Metros">Metros</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell sx={{ py: 0.18, px: 0.42 }}>
                        <TextField
                          size="small"
                          placeholder="Comentario línea..."
                          value={item.nota || ""}
                          onChange={(e) => onUpdateItem(item.codigo, { nota: e.target.value })}
                          sx={orderSingleLineFieldSx}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.18, px: 0.42, textAlign: "right" }}>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutline sx={{ fontSize: 15 }} />}
                          onClick={() => onRemoveItem(item.codigo)}
                          sx={{
                            minWidth: 0,
                            px: 0.2,
                            py: 0.05,
                            fontWeight: 600,
                            fontSize: "0.72rem",
                            textTransform: "none",
                          }}>
                          Quitar
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {shouldGroup ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{
                        py: 0.32,
                        px: 0.55,
                        backgroundColor: groupStyle.rowBg,
                        borderTop: `1px dashed ${groupStyle.headerBorder}`,
                      }}>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            md: "180px minmax(0, 1fr)",
                          },
                          gap: 0.55,
                          alignItems: "start",
                        }}>
                        <Typography
                          sx={{
                            fontSize: "0.58rem",
                            fontWeight: 800,
                            letterSpacing: "0.04em",
                            textTransform: "uppercase",
                            color: groupStyle.label,
                            pt: 0.3,
                          }}>
                          Comentario grupo
                        </Typography>
                        <TextField
                          multiline
                          minRows={1}
                          maxRows={2}
                          placeholder="Escribir nota (común para este proveedor)..."
                          value={groupNoteValue}
                          onChange={(e) => {
                            const next = e.target.value;
                            entry.items.forEach((entryItem) => onUpdateItem(entryItem.codigo, { notaGrupo: next }));
                          }}
                          sx={orderNoteFieldSx}
                          fullWidth
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : null}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const OrderDraftList = ({
  items,
  availableProviderOptions,
  onUpdateItem,
  onRemoveItem,
  onStepQuantity,
}) => {
  if (items.length === 0) {
    return (
      <Box
        sx={{
          minHeight: 220,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 1.4,
          border: `1px dashed ${BORDER_COLORS.LIGHT}`,
          backgroundColor: "rgba(255,255,255,0.82)",
        }}>
        <Typography variant="body2" color="text.secondary">
          Añade códigos desde panel izquierdo.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        columnCount: {
          xs: 1,
          lg: 2,
          xl: 4,
        },
        columnGap: 0.9,
      }}>
      {items.map((item) => (
        <Box
          key={item.codigo}
          sx={{
            breakInside: "avoid",
            WebkitColumnBreakInside: "avoid",
            pageBreakInside: "avoid",
            display: "inline-block",
            width: "100%",
            mb: 0.9,
          }}>
          <OrderDraftItemCard
            item={item}
            availableProviderOptions={availableProviderOptions}
            onUpdateItem={onUpdateItem}
            onRemoveItem={onRemoveItem}
            onStepQuantity={onStepQuantity}
          />
        </Box>
      ))}
    </Box>
  );
};

const CodePickerField = ({
  search,
  options,
  items,
  sortDirection,
  listMaxHeight,
  onSearchChange,
  onSortDirectionChange,
  onToggleCode,
}) => {
  const selectedCodes = new Set(items.map((item) => item.codigo));
  const filteredOptions = options
    .filter((option) => option.codigo.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => {
      const av = parseNumeric(a.total) || 0;
      const bv = parseNumeric(b.total) || 0;
      return sortDirection === "asc" ? av - bv : bv - av;
    });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minHeight: 0, flex: 1 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar o filtrar códigos..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={compactInputSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "rgba(0,0,0,0.45)", fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          value={sortDirection}
          onChange={(e) => onSortDirectionChange(e.target.value)}
          sx={compactInputSx}>
          <MenuItem value="desc">Mayor a menor</MenuItem>
          <MenuItem value="asc">Menor a mayor</MenuItem>
        </TextField>
      </Box>

      <List
        dense
        sx={{
          maxHeight: listMaxHeight ?? 410,
          overflow: "auto",
          borderRadius: 1.2,
          py: 0,
          backgroundColor: "#fff",
          border: "1px solid rgba(23, 64, 69, 0.18)",
          boxShadow: "0 10px 20px rgba(23, 64, 69, 0.08)",
          flex: 1,
          minHeight: 0,
        }}>
        {filteredOptions.length === 0 ? (
          <ListItem>
            <ListItemText
              primary="No se encontraron códigos."
              primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
            />
          </ListItem>
        ) : (
          filteredOptions.map((option) => {
            const checked = selectedCodes.has(option.codigo);
            return (
              <ListItem key={option.codigo} disablePadding>
                <ListItemButton
                  dense
                  onClick={() => onToggleCode(option)}
                  sx={{
                    py: 0.65,
                    borderBottom: `1px solid rgba(0,0,0,0.04)`,
                  }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1 }}>
                    <Typography sx={{ fontSize: "0.83rem", fontWeight: 700, color: BRAND_COLORS.PRIMARY_DARK || "#174045" }}>
                      {option.codigo}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        color:
                          (parseNumeric(option.total) || 0) >= 0
                            ? "#1e7a46"
                            : "#c62828",
                        minWidth: 52,
                        textAlign: "right",
                      }}>
                      {formatNumeric(option.total)}
                    </Typography>
                  </Box>
                  <Switch
                    edge="end"
                    size="small"
                    checked={checked}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onToggleCode(option)}
                  />
                </ListItemButton>
              </ListItem>
            );
          })
        )}
      </List>
    </Box>
  );
};

const OrderFormContent = ({
  codePickerSearch,
  codeOptions,
  orderItems,
  availableProviderOptions,
  usuario,
  isSending,
  codeSortDirection,
  onCodePickerSearchChange,
  onCodeSortDirectionChange,
  onToggleCode,
  onUpdateItem,
  onRemoveItem,
  onStepQuantity,
  onPreviewEmail,
  onSend,
  onClearDraft,
  onCancel,
}) => (
  <Box
    sx={{
      ...orderFormShellSx,
      gap: 1.5,
      height: { xs: "auto", lg: "72vh" },
      minHeight: 0,
    }}>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          lg: "minmax(180px, 0.58fr) minmax(0, 1.92fr)",
          xl: "minmax(190px, 0.52fr) minmax(0, 1.98fr)",
        },
        gap: 1.1,
        alignItems: "stretch",
        flex: 1,
        minHeight: 0,
      }}>
      <Box sx={{ ...orderSectionSx, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Box sx={orderSectionHeaderSx}>
          <Typography sx={{ ...orderSectionTitleSx, fontSize: "0.78rem" }}>Selección de códigos</Typography>
        </Box>
        <CodePickerField
          search={codePickerSearch}
          options={codeOptions}
          items={orderItems}
          sortDirection={codeSortDirection}
          listMaxHeight={{ xs: "28vh", md: "34vh", lg: "100%" }}
          onSearchChange={onCodePickerSearchChange}
          onSortDirectionChange={onCodeSortDirectionChange}
          onToggleCode={onToggleCode}
        />
      </Box>

      <Box sx={{ ...orderSectionSx, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Box sx={{ ...orderSectionHeaderSx, mb: 1.25 }}>
          <Box>
            <Typography sx={{ ...orderSectionTitleSx, fontSize: "0.78rem" }}>Detalle del pedido</Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", mt: 0.2 }}>
              {orderItems.length} items, {orderItems.length} líneas activas
            </Typography>
          </Box>
          <Box sx={{ minWidth: { xs: 0, md: 190, xl: 210 } }}>
            <Box
              sx={{
                height: 34,
                px: 1,
                borderRadius: 1.2,
                border: "1px solid rgba(23, 64, 69, 0.18)",
                backgroundColor: "#fff",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}>
              <PersonOutline sx={{ fontSize: 16, color: "rgba(0,0,0,0.48)" }} />
              <Typography sx={{ fontSize: "0.78rem", color: BRAND_COLORS.PRIMARY_DARK || "#174045" }}>
                {usuario || "—"}
              </Typography>
            </Box>
            <Typography sx={{ mt: 0.3, fontSize: "0.66rem", color: "text.secondary", textAlign: "right" }}>
              Backend: según referencia
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            pr: 0.5,
          }}>
          <OrderDraftGroupedList
            items={orderItems}
            availableProviderOptions={availableProviderOptions}
            onUpdateItem={onUpdateItem}
            onRemoveItem={onRemoveItem}
            onStepQuantity={onStepQuantity}
            onPreviewEmail={onPreviewEmail}
          />
        </Box>
      </Box>
    </Box>

    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        flexWrap: "wrap",
        borderTop: `1px solid ${BORDER_COLORS.LIGHT}`,
        pt: 1.1,
      }}>
      <Box>
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: BRAND_COLORS.PRIMARY_DARK || "#174045" }}>
          Total items: {orderItems.length}
        </Typography>
        <Typography sx={{ fontSize: "0.76rem", color: "text.secondary" }}>
          Estado: En revisión
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{
            minWidth: 120,
            borderRadius: 1.2,
            textTransform: "none",
            fontWeight: 700,
          }}>
          Cancelar
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClearDraft}
          disabled={orderItems.length === 0}
          startIcon={<DeleteOutline sx={{ fontSize: 16 }} />}
          sx={{
            minWidth: 140,
            borderRadius: 1.2,
            textTransform: "none",
            fontWeight: 700,
          }}>
          Limpiar pedido
        </Button>
        <Button
          variant="contained"
          onClick={onSend}
          disabled={isSending}
          sx={{
            minWidth: 150,
            borderRadius: 1.2,
            px: 2.5,
            py: 1,
            fontWeight: 700,
            boxShadow: "0 10px 24px rgba(25, 118, 210, 0.28)",
          }}>
          {isSending ? "Enviando..." : "Enviar pedido"}
        </Button>
      </Box>
    </Box>
  </Box>
);

export default function ComponentStockFornituras() {
  const lang = useSelector((state) => state.lang.value);
  const usuario = useSelector((state) => state.user.name);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [orderHistory, setOrderHistory] = useState(() => []);
  const [orderHistoryLoading, setOrderHistoryLoading] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [historyStatusFilter, setHistoryStatusFilter] = useState("all");
  const [historyDetailModalOpen, setHistoryDetailModalOpen] = useState(false);
  const [historyDetailLoading, setHistoryDetailLoading] = useState(false);
  const [selectedOrderDetail, setSelectedOrderDetail] = useState(null);
  const [confirmingReceiveBatchUid, setConfirmingReceiveBatchUid] = useState("");
  const [confirmingReceiveProviderKey, setConfirmingReceiveProviderKey] = useState("");
  const [confirmingReceiveLineKey, setConfirmingReceiveLineKey] = useState("");
  const [editingLineKey, setEditingLineKey] = useState("");
  const [partialReceiveDialog, setPartialReceiveDialog] = useState(() =>
    createEmptyPartialReceiveDialogState(usuario)
  );
  const [submittingPartialReceive, setSubmittingPartialReceive] = useState(false);
  const [editQuantityDialog, setEditQuantityDialog] = useState(() =>
    createEmptyEditQuantityDialogState()
  );
  const [submittingEditQuantity, setSubmittingEditQuantity] = useState(false);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [emailPreview, setEmailPreview] = useState({
    open: false,
    title: "",
    html: "",
    editedHtml: "",
  });
  const emailPreviewContentRef = useRef(null);
  const setEmailPreviewContentNode = useCallback(
    (node) => {
      emailPreviewContentRef.current = node;
      if (node) {
        node.innerHTML = emailPreview.editedHtml || emailPreview.html || "";
      }
    },
    [emailPreview.editedHtml, emailPreview.html]
  );
  const prevOrderModalOpenRef = useRef(false);
  const [isSendingOrder, setIsSendingOrder] = useState(false);
  const [codePickerSearch, setCodePickerSearch] = useState("");
  const [codeSortDirection, setCodeSortDirection] = useState("desc");

  const loadOrderHistory = useCallback(async ({ showErrorNotification = false } = {}) => {
    setOrderHistoryLoading(true);
    try {
      const response = await listForniturasPedidos();
      if (response?.status !== "ok") {
        if (showErrorNotification) {
          notify.error(response?.message || "No se pudo cargar el histórico de pedidos.");
        }
        return false;
      }

      const nextRows = Array.isArray(response?.data)
        ? response.data.map((order) => normalizeHistoryOrder(order))
        : [];
      setOrderHistory(nextRows);
      return true;
    } finally {
      setOrderHistoryLoading(false);
    }
  }, []);

  const closePartialReceiveDialog = useCallback(() => {
    if (submittingPartialReceive) return;
    setPartialReceiveDialog(createEmptyPartialReceiveDialogState(usuario));
  }, [submittingPartialReceive, usuario]);

  const closeEditQuantityDialog = useCallback(() => {
    if (submittingEditQuantity) return;
    setEditQuantityDialog(createEmptyEditQuantityDialogState());
  }, [submittingEditQuantity]);

  const refreshOrderHistoryAndDetail = useCallback(
    async (batchUid, pedidoId = null) => {
      await loadOrderHistory();

      if (selectedOrderDetail?.batch_uid !== batchUid) {
        return;
      }

      const detailResponse = await getForniturasPedidoDetail({
        batch_uid: batchUid,
        pedido_id: pedidoId,
      });
      if (detailResponse?.status === "ok" && detailResponse?.data) {
        setSelectedOrderDetail(normalizeHistoryOrder(detailResponse.data));
      }
    },
    [loadOrderHistory, selectedOrderDetail]
  );

  const buildReceiveLineErrorMessage = useCallback((response, fallbackPending = null) => {
    const pendingFromError =
      parseNumeric(response?.data?.pendiente) ??
      parseNumeric(response?.data?.data?.pendiente);
    const pending = pendingFromError ?? fallbackPending;

    if (response?.statusCode === 409) {
      return response?.message || "La línea ya está recepcionada por completo.";
    }

    if (response?.statusCode === 400) {
      if (pending !== null) {
        return `La cantidad indicada no es válida o supera lo pendiente (${formatQuantityValue(pending)}).`;
      }
      return response?.message || "La cantidad indicada no es válida.";
    }

    return response?.message || "No se pudo confirmar la recepción de la línea.";
  }, []);

  const buildEditQuantityErrorMessage = useCallback((response) => {
    if (response?.statusCode === 409) {
      return response?.message || "La línea ya no se puede editar.";
    }

    if (response?.statusCode === 400) {
      return response?.message || "La cantidad indicada no es válida.";
    }

    return response?.message || "No se pudo actualizar la cantidad de la línea.";
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      const data = await getStockFornituras();
      if (cancelled) return;
      if (data === false) {
        setError("No se pudieron cargar los datos de fornituras");
        setRows([]);
      } else {
        setRows(Array.isArray(data) ? data : []);
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadOrderHistory();
  }, [loadOrderHistory]);

  const mergedRows = useMemo(() => {
    const grouped = new Map();

    rows.forEach((row) => {
      const codigo = getRowCodigo(row);
      if (!codigo) return;

      const providerOption = getProviderOptionFromRow(row);
      if (!grouped.has(codigo)) {
        const unidades = parseNumeric(row?.unidades) || 0;
        const sto1990 = parseNumeric(row?.sto_1990) || 0;
        const sto1987 = parseNumeric(row?.sto_1987) || 0;
        const unidadesPedidasPendientes =
          parseNumeric(row?.unidades_pedidas_pendientes) ??
          parseNumeric(row?.unidadesPedidasPendientes) ??
          parseNumeric(row?.UNIDADES_PEDIDAS_PENDIENTES) ??
          0;
        const stockEur =
          parseNumeric(row?.stock_eur) ??
          parseNumeric(row?.stock) ??
          parseNumeric(row?.Stock) ??
          parseNumeric(row?.STOCK) ??
          0;
        const stockWt =
          parseNumeric(row?.stock_wt) ??
          parseNumeric(row?.stockWt) ??
          parseNumeric(row?.Stock_Wt) ??
          parseNumeric(row?.STOCK_WT) ??
          0;

        grouped.set(codigo, {
          codigo,
          unidades,
          sto_1990: sto1990,
          sto_1987: sto1987,
          stock_eur: stockEur,
          stock_wt: stockWt,
          total: sto1990 + sto1987 + stockEur - unidades,
          unidades_pedidas_pendientes: unidadesPedidasPendientes,
          providerOptions: providerOption ? [providerOption] : [],
        });
        return;
      }

      if (!providerOption) return;
      const current = grouped.get(codigo);
      if (!current.providerOptions.some((provider) => provider.key === providerOption.key)) {
        current.providerOptions.push(providerOption);
      }
    });

    return Array.from(grouped.values())
      .sort((a, b) =>
        String(a.codigo).localeCompare(String(b.codigo), undefined, {
          numeric: true,
          sensitivity: "base",
        })
      );
  }, [rows]);

  const codeOptions = useMemo(
    () =>
      mergedRows.map((row) => ({
        codigo: row.codigo,
        unidades: row.unidades,
        total: row.total,
        providerOptions: row.providerOptions || [],
      })),
    [mergedRows]
  );

  const allProviderOptions = useMemo(
    () => mergeProviderOptions(...mergedRows.map((row) => row.providerOptions || [])),
    [mergedRows]
  );

  const filteredOrderHistory = useMemo(() => {
    const q = String(historySearch ?? "").trim().toLowerCase();
    return orderHistory.filter((row) => {
      if (historyStatusFilter !== "all" && row?.estado?.id !== historyStatusFilter) return false;
      if (!q) return true;
      const haystack = [
        row?.proveedor,
        row?.pedido,
        row?.usuario,
        row?.batch_uid,
        row?.pedido_id,
        row?.estado?.label,
      ]
        .map((value) => String(value ?? "").toLowerCase())
        .join("\u0000");
      return haystack.includes(q);
    });
  }, [historySearch, historyStatusFilter, orderHistory]);

  useEffect(() => {
    const wasOpen = prevOrderModalOpenRef.current;
    prevOrderModalOpenRef.current = orderModalOpen;
    if (wasOpen || !orderModalOpen) return;

    const byCode = mergedRows
      .map((row) => {
        const providersWithRef = (row?.providerOptions || [])
          .filter((p) => String(p?.referenciaprov || "").trim() !== "")
          .map((p) => ({
            nombre: p?.nombre ?? "",
            referenciaprov: p?.referenciaprov ?? "",
          }));
        return { codigo: row?.codigo ?? "", proveedores: providersWithRef };
      })
      .filter((entry) => entry.codigo && entry.proveedores.length > 1);

    console.groupCollapsed(
      "[StockFornituras] referencias con >1 proveedor (con referenciaprov)"
    );
    console.log(byCode);
    console.groupEnd();
  }, [mergedRows, orderModalOpen]);

  const addCodeToOrder = useCallback(
    (option) => {
      if (!option?.codigo) return;
      setOrderItems((prev) => {
        const existingIndex = prev.findIndex((item) => item.codigo === option.codigo);
        if (existingIndex >= 0) {
          return prev.map((item, index) =>
            index === existingIndex
              ? {
                  ...item,
                  cantidad: String((parseNumeric(item.cantidad) || 0) + 1),
                }
              : item
          );
        }

        const providerOptions = mergeProviderOptions(option.providerOptions, allProviderOptions);
        const defaultProvider = option.providerOptions?.[0] || providerOptions[0] || null;
        return [
          ...prev,
          {
            codigo: option.codigo,
            cantidad: "1",
            unidad: "Unidades",
            nota: "",
            notaGrupo: "",
            providerOptions,
            providerKey: defaultProvider?.key || "",
            proveedor: defaultProvider?.proveedor ?? "",
            proveedorNombre: defaultProvider?.nombre ?? "",
            referenciaprov: defaultProvider?.referenciaprov ?? "",
          },
        ];
      });
    },
    [allProviderOptions]
  );

  const toggleCodeFromPicker = useCallback(
    (option) => {
      if (!option?.codigo) return;
      const exists = orderItems.some((item) => item.codigo === option.codigo);
      if (exists) {
        setOrderItems((prev) => prev.filter((item) => item.codigo !== option.codigo));
        return;
      }
      addCodeToOrder(option);
    },
    [addCodeToOrder, orderItems]
  );

  const updateOrderItem = useCallback((codigo, patch) => {
    setOrderItems((prev) =>
      prev.map((item) => {
        if (item.codigo !== codigo) return item;
        const nextItem = { ...item, ...patch };
        const mergedItemProviders = mergeProviderOptions(nextItem.providerOptions, allProviderOptions);
        nextItem.providerOptions = mergedItemProviders;
        if (patch.providerKey && mergedItemProviders.length > 0) {
          const selectedProviderAny = mergedItemProviders.find((provider) => provider.key === patch.providerKey);
          if (selectedProviderAny) {
            nextItem.proveedor = selectedProviderAny.proveedor;
            nextItem.proveedorNombre = selectedProviderAny.nombre;
          }

          const codeRow = mergedRows.find((row) => row.codigo === codigo);
          const selectedProviderForCode = (codeRow?.providerOptions || []).find(
            (provider) => provider.key === patch.providerKey && String(provider?.referenciaprov || "").trim() !== ""
          );
          nextItem.referenciaprov = selectedProviderForCode?.referenciaprov ?? "";
        }
        return nextItem;
      })
    );
  }, [allProviderOptions, mergedRows]);

  const removeOrderItem = useCallback((codigo) => {
    setOrderItems((prev) => prev.filter((item) => item.codigo !== codigo));
  }, []);

  const stepOrderItemQuantity = useCallback((codigo, delta) => {
    setOrderItems((prev) =>
      prev.map((item) => {
        if (item.codigo !== codigo) return item;
        const nextValue = Math.max(1, (parseNumeric(item.cantidad) || 1) + delta);
        return { ...item, cantidad: String(nextValue) };
      })
    );
  }, []);

  const clearOrderDraft = useCallback(() => {
    setOrderItems([]);
    setCodePickerSearch("");
  }, []);

  const buildOrderOptionFromRow = useCallback((row) => {
    if (!row?.codigo) return null;
    return {
      codigo: row.codigo,
      unidades: row.unidades,
      total: row.total,
      providerOptions: row.providerOptions || [],
    };
  }, []);

  const handleOpenEmailPreview = useCallback(({ title, html }) => {
    setEmailPreview({
      open: true,
      title: String(title ?? "Correo proveedor"),
      html: String(html ?? ""),
      editedHtml: String(html ?? ""),
    });
  }, []);

  const handleCloseEmailPreview = useCallback(() => {
    setEmailPreview((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  const handleCopyEmailPreview = useCallback(async () => {
    const html = String(
      emailPreviewContentRef.current?.innerHTML ?? emailPreview.editedHtml ?? emailPreview.html ?? ""
    );
    if (!html) {
      notify.warn("No hay contenido para copiar.");
      return;
    }

    try {
      const plainText = buildEmailPlainTextFromHtml(html);
      if (navigator?.clipboard?.write && typeof window !== "undefined" && window.ClipboardItem) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([plainText], { type: "text/plain" }),
          }),
        ]);
      } else if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(html);
      } else {
        throw new Error("Clipboard API no disponible");
      }
      notify.success("Correo copiado.");
    } catch (error) {
      console.error("No se pudo copiar el correo:", error);
      notify.error("No se pudo copiar el correo.");
    }
  }, [emailPreview.editedHtml, emailPreview.html]);

  useEffect(() => {
    if (!emailPreview.open) return;
    const applyPreviewHtml = () => {
      if (!emailPreviewContentRef.current) return;
      emailPreviewContentRef.current.innerHTML = emailPreview.editedHtml || emailPreview.html || "";
    };
    const frameId = window.requestAnimationFrame(applyPreviewHtml);
    return () => window.cancelAnimationFrame(frameId);
  }, [emailPreview.editedHtml, emailPreview.html, emailPreview.open]);

  const cancelOrderAndClose = useCallback(() => {
    clearOrderDraft();
    setOrderModalOpen(false);
  }, [clearOrderDraft]);

  const handleAppendRowToOrder = useCallback(
    (row) => {
      const option = buildOrderOptionFromRow(row);
      if (!option) return;
      addCodeToOrder(option);
    },
    [addCodeToOrder, buildOrderOptionFromRow]
  );

  const handleRemoveOneRowFromOrder = useCallback((row) => {
    const codigo = String(row?.codigo ?? "").trim();
    if (!codigo) return;
    setOrderItems((prev) =>
      prev.flatMap((item) => {
        if (item.codigo !== codigo) return [item];
        const nextQuantity = Math.max(0, (parseNumeric(item.cantidad) || 0) - 1);
        if (nextQuantity <= 0) return [];
        return [{ ...item, cantidad: String(nextQuantity) }];
      })
    );
  }, []);

  const handleCreateOrderFromRow = useCallback(
    (row) => {
      const option = buildOrderOptionFromRow(row);
      if (!option) return;
      addCodeToOrder(option);
      setOrderModalOpen(true);
    },
    [addCodeToOrder, buildOrderOptionFromRow]
  );

  const handleSendOrder = useCallback(async () => {
    if (isSendingOrder) return;

    if (!orderItems.length) {
      notify.warn("Añade al menos un código.");
      return;
    }

    const normalizedItems = orderItems
      .map((item) => ({
        ...item,
        cantidad: parseNumeric(item.cantidad) || 0,
        unidad: item.unidad || "Unidades",
        proveedor: item.proveedor ?? "",
        proveedorNombre: item.proveedorNombre ?? "",
        referenciaprov: item.referenciaprov ?? "",
        notaGrupo: item.notaGrupo ?? "",
      }))
      .filter((item) => item.cantidad > 0);

    if (!normalizedItems.length) {
      notify.warn("Cantidad debe ser mayor que 0.");
      return;
    }

    const orderPayload = {
      screen: "forniturasPedidos",
      action: "create",
      usuario: usuario || "—",
      items: normalizedItems.map((item) => ({
        codigo: item.codigo,
        nombre: item.proveedorNombre || "",
        referenciaprov: item.referenciaprov || "",
        cantidad: item.cantidad,
        unidad: item.unidad,
        nota: [
          String(item.notaGrupo || "").trim(),
          String(item.nota || "").trim(),
        ]
          .filter(Boolean)
          .join(" | "),
      })),
    };

    console.log("Payload enviar pedido:", orderPayload);
    console.log("Payload enviar pedido JSON:", JSON.stringify(orderPayload, null, 2));

    setIsSendingOrder(true);
    try {
      const response = await createForniturasPedido(orderPayload);
      if (response?.status !== "ok") {
        notify.error(response?.message || "No se pudo guardar el pedido.");
        return;
      }

      const createdHistoryEntry = normalizeHistoryOrder({
        pedido_id: response?.pedido_id ?? null,
        batch_uid: response?.batch_uid ?? "",
        usuario: orderPayload.usuario,
        fecha_alta: new Date().toISOString(),
        recibido_fecha: null,
        items: orderPayload.items,
      });
      const historyReloaded = await loadOrderHistory();
      if (!historyReloaded) {
        setOrderHistory((prev) => [createdHistoryEntry, ...prev]);
      }
      clearOrderDraft();
      setOrderModalOpen(false);
      notify.success(
        response?.message ||
          `Pedido guardado correctamente. ID ${response?.pedido_id ?? ""}`.trim()
      );
      console.log("Respuesta enviar pedido:", response);
    } finally {
      setIsSendingOrder(false);
    }
  }, [clearOrderDraft, isSendingOrder, loadOrderHistory, orderItems, usuario]);

  const handleOpenHistoryDetail = useCallback(async (row) => {
    if (!row) return;

    setSelectedOrderDetail(row);
    setHistoryDetailModalOpen(true);
    setHistoryDetailLoading(true);
    try {
      const response = await getForniturasPedidoDetail({
        pedido_id: row.pedido_id,
        batch_uid: row.batch_uid,
      });
      if (response?.status !== "ok" || !response?.data) {
        notify.error(response?.message || "No se pudo cargar el detalle del pedido.");
        return;
      }

      setSelectedOrderDetail(normalizeHistoryOrder(response.data));
    } finally {
      setHistoryDetailLoading(false);
    }
  }, []);

  const handleOpenPartialReceiveDialog = useCallback(
    (row, lineItem) => {
      const batchUid = String(row?.batch_uid ?? "").trim();
      const lineaId = Number(lineItem?.linea_id) || 0;
      const pendingQty = getLinePendingQuantity(lineItem);

      if (!batchUid || !lineaId) {
        notify.error("La línea seleccionada no es válida.");
        return;
      }
      if (lineItem?.recibidoFecha) {
        notify.info("La línea ya está recepcionada.");
        return;
      }
      if (!(pendingQty > 0)) {
        notify.warn("No queda cantidad pendiente para esta línea.");
        return;
      }

      setPartialReceiveDialog({
        open: true,
        batchUid,
        pedidoId: row?.pedido_id ?? null,
        lineItem,
        cantidad: "",
        usuario: String(usuario ?? "").trim(),
        nota: "",
      });
    },
    [usuario]
  );

  const handleOpenEditQuantityDialog = useCallback((row, lineItem) => {
    const batchUid = String(row?.batch_uid ?? "").trim();
    const lineaId = Number(lineItem?.linea_id) || 0;
    const receivedQty = getLineReceivedTotal(lineItem);

    if (!batchUid || !lineaId) {
      notify.error("La línea seleccionada no es válida.");
      return;
    }
    if (lineItem?.recibidoFecha || receivedQty > 0) {
      notify.warn("No se puede editar una línea con recepciones registradas.");
      return;
    }

    setEditQuantityDialog({
      open: true,
      batchUid,
      pedidoId: row?.pedido_id ?? null,
      lineItem,
      cantidad: String(parseNumeric(lineItem?.cantidad) ?? lineItem?.cantidad ?? ""),
    });
  }, []);

  const handleConfirmReceive = useCallback(async (row) => {
    const batchUid = String(row?.batch_uid ?? "").trim();
    if (!batchUid) {
      notify.error("El pedido no tiene batch UID válido.");
      return;
    }

    if (confirmingReceiveBatchUid === batchUid || row?.recibidoFecha) {
      return;
    }

    setConfirmingReceiveBatchUid(batchUid);
    try {
      const response = await confirmReceiveForniturasPedido({ batch_uid: batchUid });
      if (response?.status !== "ok" || !response?.data) {
        notify.error(response?.message || "No se pudo confirmar la recepción.");
        return;
      }

      const recibidoFecha = response.data?.recibido_fecha ?? null;
      setOrderHistory((prev) =>
        prev.map((item) =>
          item.batch_uid === batchUid
            ? {
                ...item,
                recibidoFecha,
              }
            : item
        )
      );
      setSelectedOrderDetail((prev) =>
        prev?.batch_uid === batchUid
          ? {
              ...prev,
              recibidoFecha,
            }
          : prev
      );

      await loadOrderHistory();
      if (selectedOrderDetail?.batch_uid === batchUid) {
        const detailResponse = await getForniturasPedidoDetail({ batch_uid: batchUid });
        if (detailResponse?.status === "ok" && detailResponse?.data) {
          setSelectedOrderDetail(normalizeHistoryOrder(detailResponse.data));
        }
      }

      notify.success(response?.message || "Recepción confirmada.");
    } finally {
      setConfirmingReceiveBatchUid("");
    }
  }, [confirmingReceiveBatchUid, loadOrderHistory, selectedOrderDetail]);

  const handleConfirmReceiveProvider = useCallback(
    async (row, proveedor) => {
      const batchUid = String(row?.batch_uid ?? "").trim();
      const proveedorNombre = String(proveedor ?? "").trim();
      const providerConfirmKey = `${batchUid}::${proveedorNombre}`;

      if (!batchUid) {
        notify.error("El pedido no tiene batch UID válido.");
        return;
      }
      if (!proveedorNombre) {
        notify.error("El proveedor no es válido.");
        return;
      }
      if (confirmingReceiveProviderKey === providerConfirmKey) {
        return;
      }

      const providerAlreadyReceived = (row?.proveedores || []).some(
        (item) => String(item?.proveedor ?? "").trim() === proveedorNombre && item?.recibidoFecha
      );
      if (providerAlreadyReceived) {
        return;
      }

      setConfirmingReceiveProviderKey(providerConfirmKey);
      try {
        const response = await confirmReceiveForniturasPedidoProveedor({
          batch_uid: batchUid,
          proveedor: proveedorNombre,
        });
        if (response?.status !== "ok") {
          notify.error(response?.message || "No se pudo confirmar la recepción del proveedor.");
          return;
        }

        const providerReceivedDate = response?.proveedor?.recibido_fecha ?? null;
        const pedidoRecibidoFecha = response?.pedido_estado?.pedido_recibido_fecha ?? row?.recibidoFecha ?? null;

        setOrderHistory((prev) =>
          prev.map((item) =>
            item.batch_uid === batchUid
              ? {
                  ...item,
                  recibidoFecha: pedidoRecibidoFecha,
                  proveedores: (item.proveedores || []).map((providerItem) =>
                    String(providerItem?.proveedor ?? "").trim() === proveedorNombre
                      ? { ...providerItem, recibidoFecha: providerReceivedDate }
                      : providerItem
                  ),
                }
              : item
          )
        );

        setSelectedOrderDetail((prev) =>
          prev?.batch_uid === batchUid
            ? {
                ...prev,
                recibidoFecha: pedidoRecibidoFecha,
                proveedores: (prev.proveedores || []).map((providerItem) =>
                  String(providerItem?.proveedor ?? "").trim() === proveedorNombre
                    ? { ...providerItem, recibidoFecha: providerReceivedDate }
                    : providerItem
                ),
              }
            : prev
        );

        await loadOrderHistory();
        if (selectedOrderDetail?.batch_uid === batchUid) {
          const detailResponse = await getForniturasPedidoDetail({ batch_uid: batchUid });
          if (detailResponse?.status === "ok" && detailResponse?.data) {
            setSelectedOrderDetail(normalizeHistoryOrder(detailResponse.data));
          }
        }

        notify.success(response?.message || "Recepción del proveedor confirmada.");
      } finally {
        setConfirmingReceiveProviderKey("");
      }
    },
    [confirmingReceiveProviderKey, loadOrderHistory, selectedOrderDetail]
  );

  const handleConfirmReceiveLine = useCallback(
    async (row, lineItem) => {
      const batchUid = String(row?.batch_uid ?? "").trim();
      const lineaId = Number(lineItem?.linea_id) || 0;
      const lineConfirmKey = getLineMutationKey(batchUid, lineItem);
      const pendingQty = getLinePendingQuantity(lineItem);

      if (!batchUid) {
        notify.error("El pedido no tiene batch UID válido.");
        return;
      }
      if (!lineaId) {
        notify.error("La línea no tiene un ID válido.");
        return;
      }
      if (!(pendingQty > 0)) {
        notify.warn("No queda cantidad pendiente para esta línea.");
        return;
      }
      if (confirmingReceiveLineKey === lineConfirmKey || lineItem?.recibidoFecha) {
        return;
      }

      setConfirmingReceiveLineKey(lineConfirmKey);
      try {
        const response = await confirmReceiveForniturasPedidoLinea({
          batch_uid: batchUid,
          linea_id: lineaId,
        });
        if (response?.status !== "ok") {
          notify.error(buildReceiveLineErrorMessage(response, pendingQty));
          return;
        }
        await refreshOrderHistoryAndDetail(batchUid, row?.pedido_id ?? null);

        notify.success(response?.message || "Recepción de línea confirmada.");
      } finally {
        setConfirmingReceiveLineKey("");
      }
    },
    [buildReceiveLineErrorMessage, confirmingReceiveLineKey, refreshOrderHistoryAndDetail]
  );

  const handleSubmitPartialReceive = useCallback(async () => {
    const batchUid = String(partialReceiveDialog.batchUid ?? "").trim();
    const lineaId = Number(partialReceiveDialog.lineItem?.linea_id) || 0;
    const pendingQty = getLinePendingQuantity(partialReceiveDialog.lineItem);
    const cantidadRecibida = parseNumeric(partialReceiveDialog.cantidad);
    const lineConfirmKey = getLineMutationKey(batchUid, partialReceiveDialog.lineItem);

    if (!batchUid || !lineaId) {
      notify.error("La línea seleccionada no es válida.");
      return;
    }
    if (cantidadRecibida === null || !(cantidadRecibida > 0)) {
      notify.warn("Indica una cantidad recibida mayor que 0.");
      return;
    }
    if (pendingQty > 0 && cantidadRecibida > pendingQty) {
      notify.warn(`La cantidad recibida no puede superar lo pendiente (${formatQuantityValue(pendingQty)}).`);
      return;
    }

    setSubmittingPartialReceive(true);
    setConfirmingReceiveLineKey(lineConfirmKey);
    try {
      const response = await confirmReceiveForniturasPedidoLinea({
        batch_uid: batchUid,
        linea_id: lineaId,
        cantidad_recibida: cantidadRecibida,
        usuario: partialReceiveDialog.usuario,
        nota: partialReceiveDialog.nota,
      });

      if (response?.status !== "ok") {
        notify.error(buildReceiveLineErrorMessage(response, pendingQty));
        return;
      }

      setPartialReceiveDialog(createEmptyPartialReceiveDialogState(usuario));
      await refreshOrderHistoryAndDetail(batchUid, partialReceiveDialog.pedidoId);
      notify.success(response?.message || "Recepción parcial registrada.");
    } finally {
      setSubmittingPartialReceive(false);
      setConfirmingReceiveLineKey("");
    }
  }, [buildReceiveLineErrorMessage, partialReceiveDialog, refreshOrderHistoryAndDetail, usuario]);

  const handleSubmitEditQuantity = useCallback(async () => {
    const batchUid = String(editQuantityDialog.batchUid ?? "").trim();
    const lineaId = Number(editQuantityDialog.lineItem?.linea_id) || 0;
    const receivedQty = getLineReceivedTotal(editQuantityDialog.lineItem);
    const nextQuantity = parseNumeric(editQuantityDialog.cantidad);
    const lineEditKey = getLineMutationKey(batchUid, editQuantityDialog.lineItem);

    if (!batchUid || !lineaId) {
      notify.error("La línea seleccionada no es válida.");
      return;
    }
    if (receivedQty > 0 || editQuantityDialog.lineItem?.recibidoFecha) {
      notify.warn("No se puede editar una línea con recepciones registradas.");
      return;
    }
    if (nextQuantity === null || !(nextQuantity > 0)) {
      notify.warn("Indica una cantidad mayor que 0.");
      return;
    }

    setSubmittingEditQuantity(true);
    setEditingLineKey(lineEditKey);
    try {
      const response = await updateForniturasPedidoLineaCantidad({
        batch_uid: batchUid,
        linea_id: lineaId,
        cantidad: nextQuantity,
      });

      if (response?.status !== "ok") {
        notify.error(buildEditQuantityErrorMessage(response));
        return;
      }

      const responseLine = response?.linea ?? response?.data?.linea ?? {};
      const emailStatus = response?.email_status ?? response?.data?.email_status;
      const emailError = response?.email_error ?? response?.data?.email_error;
      const cantidadOld = formatQuantityValue(responseLine?.cantidad_old);
      const cantidadNew = formatQuantityValue(responseLine?.cantidad_new ?? nextQuantity);

      setEditQuantityDialog(createEmptyEditQuantityDialogState());
      await refreshOrderHistoryAndDetail(batchUid, editQuantityDialog.pedidoId);
      notify.success(response?.message || `Cantidad actualizada de ${cantidadOld} a ${cantidadNew}.`);

      if (emailStatus === "error") {
        notify.warn(emailError || "La cantidad se actualizó, pero falló el correo de notificación.");
      }
    } finally {
      setSubmittingEditQuantity(false);
      setEditingLineKey("");
    }
  }, [buildEditQuantityErrorMessage, editQuantityDialog, refreshOrderHistoryAndDetail]);

  if (error && !loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, width: "100%", boxSizing: "border-box" }}>
      <Box
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            xl: "minmax(0, 1.7fr) minmax(360px, 0.95fr)",
          },
          gap: 2,
          alignItems: "stretch",
        }}>
        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
          <StockTable
            title="Stock Fornituras"
            sourceRows={mergedRows}
            lang={lang}
            loading={loading}
            columns={MERGED_COLUMNS}
            exportFileBase="Stock_Fornituras"
            sheetName="Stock Fornituras"
            highlightCordobaRequirementGap
            onCreateOrderFromRow={handleCreateOrderFromRow}
            onAppendRowToOrder={handleAppendRowToOrder}
            onRemoveOneFromOrder={handleRemoveOneRowFromOrder}
            orderDraftQuantities={Object.fromEntries(orderItems.map((item) => [item.codigo, item.cantidad]))}
          />
        </Box>

        <Box sx={{ position: { xs: "static", xl: "relative" }, minWidth: 0 }}>
          <Box
            sx={{
              position: { xs: "static", xl: "absolute" },
              top: { xl: 0 },
              left: { xl: 0 },
              right: { xl: 0 },
              bottom: { xl: 0 },
              display: "flex",
              flexDirection: "column",
              gap: 2,
              overflow: { xs: "visible", xl: "hidden" },
            }}>
          <SidePanel title="Nuevo pedido" sx={{ flex: "0 0 auto" }}>
            <Box
              onClick={() => setOrderModalOpen(true)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                py: 1.25,
                px: 1.5,
                borderRadius: 1.2,
                border: "1px dashed rgba(25, 118, 210, 0.35)",
                backgroundColor: "rgba(25, 118, 210, 0.04)",
                color: "#1565c0",
                cursor: "pointer",
                fontWeight: 700,
                transition:
                  "transform 140ms ease, box-shadow 140ms ease, background-color 140ms ease, border-color 140ms ease",
                boxShadow: "0 4px 10px rgba(21, 101, 192, 0.08)",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.10)",
                  border: "1px dashed rgba(25, 118, 210, 0.55)",
                  boxShadow: "0 10px 22px rgba(21, 101, 192, 0.16)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                  boxShadow: "0 6px 12px rgba(21, 101, 192, 0.12)",
                },
              }}>
              <Add sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: "0.88rem", fontWeight: 700 }}>
                Nuevo pedido
              </Typography>
            </Box>
          </SidePanel>

          <SidePanel
            title="Histórico pedidos"
            sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}
            contentSx={{ p: 0, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
            action={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="caption" sx={{ opacity: 0.88 }}>
                  {orderHistoryLoading ? "Cargando..." : `${filteredOrderHistory.length} registros`}
                </Typography>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => setHistoryModalOpen(true)}
                  sx={{ minWidth: 0, px: 0.75 }}>
                  Ver
                </Button>
              </Box>
            }>
            <HistoryFiltersBar
              search={historySearch}
              status={historyStatusFilter}
              onSearchChange={setHistorySearch}
              onStatusChange={setHistoryStatusFilter}
              filteredCount={filteredOrderHistory.length}
              totalCount={orderHistory.length}
            />
            <OrderHistoryTable
              rows={filteredOrderHistory}
              maxHeight="none"
              framed={false}
              onRowClick={handleOpenHistoryDetail}
              onConfirmReceive={handleConfirmReceive}
              confirmingBatchUid={confirmingReceiveBatchUid}
              tableSx={{ flex: 1, minHeight: 0 }}
            />
          </SidePanel>
          </Box>
        </Box>
      </Box>

      <PanelDialog
        open={historyModalOpen}
        title="Histórico pedidos"
        onClose={() => setHistoryModalOpen(false)}
        maxWidth="xl">
        <HistoryFiltersBar
          search={historySearch}
          status={historyStatusFilter}
          onSearchChange={setHistorySearch}
          onStatusChange={setHistoryStatusFilter}
          filteredCount={filteredOrderHistory.length}
          totalCount={orderHistory.length}
        />
        <OrderHistoryTable
          rows={filteredOrderHistory}
          maxHeight="70vh"
          framed={false}
          onRowClick={handleOpenHistoryDetail}
          onConfirmReceive={handleConfirmReceive}
          confirmingBatchUid={confirmingReceiveBatchUid}
        />
      </PanelDialog>

      <PanelDialog
        open={historyDetailModalOpen}
        title="Detalle pedido"
        onClose={() => setHistoryDetailModalOpen(false)}
        maxWidth="xl">
        <OrderHistoryDetailContent
          order={selectedOrderDetail}
          loading={historyDetailLoading}
          onConfirmReceiveProvider={handleConfirmReceiveProvider}
          confirmingProviderKey={confirmingReceiveProviderKey}
          onConfirmReceiveLine={handleConfirmReceiveLine}
          confirmingLineKey={confirmingReceiveLineKey}
          onOpenPartialReceiveLine={handleOpenPartialReceiveDialog}
          onOpenEditLineQuantity={handleOpenEditQuantityDialog}
          editingLineKey={editingLineKey}
          onPreviewEmail={handleOpenEmailPreview}
        />
      </PanelDialog>

      <Dialog
        open={partialReceiveDialog.open}
        onClose={closePartialReceiveDialog}
        fullWidth
        maxWidth="sm">
        <DialogTitle>Recepción parcial</DialogTitle>
        <DialogContent dividers sx={{ display: "grid", gap: 1.2 }}>
          <Box sx={{ display: "grid", gap: 0.45 }}>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700 }}>
              {partialReceiveDialog.lineItem?.codigo || "—"} · {partialReceiveDialog.lineItem?.nombre || "Sin nombre"}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
              Pedido: {formatQuantityValue(partialReceiveDialog.lineItem?.cantidad)} {partialReceiveDialog.lineItem?.unidad || ""}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
              Recepcionado: {formatQuantityValue(getLineReceivedTotal(partialReceiveDialog.lineItem))}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
              Pendiente: {formatQuantityValue(getLinePendingQuantity(partialReceiveDialog.lineItem))}
            </Typography>
          </Box>

          <TextField
            label="Cantidad recibida"
            value={partialReceiveDialog.cantidad}
            onChange={(event) =>
              setPartialReceiveDialog((prev) => ({
                ...prev,
                cantidad: event.target.value,
              }))
            }
            placeholder="Ej. 100"
            disabled={submittingPartialReceive}
            fullWidth
          />
          <TextField
            label="Usuario"
            value={partialReceiveDialog.usuario}
            onChange={(event) =>
              setPartialReceiveDialog((prev) => ({
                ...prev,
                usuario: event.target.value,
              }))
            }
            disabled={submittingPartialReceive}
            fullWidth
          />
          <TextField
            label="Nota"
            value={partialReceiveDialog.nota}
            onChange={(event) =>
              setPartialReceiveDialog((prev) => ({
                ...prev,
                nota: event.target.value,
              }))
            }
            disabled={submittingPartialReceive}
            multiline
            minRows={3}
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closePartialReceiveDialog} disabled={submittingPartialReceive}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSubmitPartialReceive} disabled={submittingPartialReceive}>
            {submittingPartialReceive ? "Guardando..." : "Registrar recepción"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editQuantityDialog.open}
        onClose={closeEditQuantityDialog}
        fullWidth
        maxWidth="sm">
        <DialogTitle>Editar cantidad pedida</DialogTitle>
        <DialogContent dividers sx={{ display: "grid", gap: 1.2 }}>
          <Box sx={{ display: "grid", gap: 0.45 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, flex: 1 }}>
                {editQuantityDialog.lineItem?.codigo || "—"} · {editQuantityDialog.lineItem?.nombre || "Sin nombre"}
              </Typography>
              <Tooltip title="Ver correo de modificación">
                <span>
                  <IconButton
                    size="small"
                    disabled={!editQuantityDialog.lineItem}
                    onClick={() =>
                      handleOpenEmailPreview({
                        title: `Modificación ${getHistoryItemProviderName(editQuantityDialog.lineItem) || "proveedor"}`,
                        html: buildForniturasProveedorEmailBodyModificacion({
                          proveedorNombre: getHistoryItemProviderName(editQuantityDialog.lineItem),
                          batchUid: editQuantityDialog.batchUid,
                          pedidoId: editQuantityDialog.pedidoId,
                          codigo: editQuantityDialog.lineItem?.codigo,
                          referenciaProv: editQuantityDialog.lineItem?.referenciaprov,
                          unidad: editQuantityDialog.lineItem?.unidad,
                          cantidadOld: editQuantityDialog.lineItem?.cantidad,
                          cantidadNew: editQuantityDialog.cantidad,
                        }),
                      })
                    }
                    sx={{ color: "rgba(23, 64, 69, 0.72)", p: 0.3 }}>
                    <MailOutline sx={{ fontSize: 17 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
              Cantidad actual: {formatQuantityValue(editQuantityDialog.lineItem?.cantidad)} {editQuantityDialog.lineItem?.unidad || ""}
            </Typography>
          </Box>

          <TextField
            label="Nueva cantidad"
            value={editQuantityDialog.cantidad}
            onChange={(event) =>
              setEditQuantityDialog((prev) => ({
                ...prev,
                cantidad: event.target.value,
              }))
            }
            placeholder="Ej. 500"
            disabled={submittingEditQuantity}
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeEditQuantityDialog} disabled={submittingEditQuantity}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSubmitEditQuantity} disabled={submittingEditQuantity}>
            {submittingEditQuantity ? "Guardando..." : "Actualizar cantidad"}
          </Button>
        </DialogActions>
      </Dialog>

      <PanelDialog
        open={orderModalOpen}
        title="Hacer pedido"
        onClose={() => setOrderModalOpen(false)}
        maxWidth="lg">
        <OrderFormContent
          codePickerSearch={codePickerSearch}
          codeOptions={codeOptions}
          orderItems={orderItems}
          availableProviderOptions={allProviderOptions}
          usuario={usuario}
          isSending={isSendingOrder}
          codeSortDirection={codeSortDirection}
          onCodePickerSearchChange={setCodePickerSearch}
          onCodeSortDirectionChange={setCodeSortDirection}
          onToggleCode={toggleCodeFromPicker}
          onUpdateItem={updateOrderItem}
          onRemoveItem={removeOrderItem}
          onStepQuantity={stepOrderItemQuantity}
          onPreviewEmail={handleOpenEmailPreview}
          onSend={handleSendOrder}
          onClearDraft={clearOrderDraft}
          onCancel={cancelOrderAndClose}
        />
      </PanelDialog>

      <Dialog
        open={emailPreview.open}
        onClose={handleCloseEmailPreview}
        fullWidth
        maxWidth="md">
        <DialogTitle
          sx={{
            pb: 1,
            pr: 12,
            display: "flex",
            alignItems: "center",
          }}>
          {emailPreview.title || "Correo proveedor"}
          <Tooltip title="Copiar HTML">
            <IconButton
              aria-label="Copiar correo"
              onClick={handleCopyEmailPreview}
              size="small"
              sx={{
                position: "absolute",
                right: 44,
                top: 12,
                color: "text.secondary",
              }}>
              <ContentCopy sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <IconButton
            aria-label="Cerrar"
            onClick={handleCloseEmailPreview}
            size="small"
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "text.secondary",
            }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Box sx={{ display: "grid", gap: 0.75 }}>
            <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
              Vista previa editable
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: "#fff", minHeight: 280 }}>
              <Box
                ref={setEmailPreviewContentNode}
                contentEditable
                suppressContentEditableWarning
                onInput={(event) => {
                  setEmailPreview((prev) => ({
                    ...prev,
                    editedHtml: event.currentTarget.innerHTML,
                  }));
                }}
                sx={{
                  minHeight: 240,
                  outline: "none",
                  fontFamily: "Arial, sans-serif",
                  color: "#222",
                  "& p": {
                    m: 0,
                    mb: 1.5,
                  },
                  "& ul": {
                    mt: 0,
                    mb: 1.5,
                    pl: 3,
                  },
                  "& li": {
                    mb: 0.5,
                  },
                }}
              />
            </Paper>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
